/**
 * @file Shared TypeScript types, constants, and utilities for the ShipStatic platform.
 * This package is the single source of truth for all shared data structures.
 */
// =============================================================================
// DEPLOYMENT TYPES
// =============================================================================
/**
 * Deployment status constants
 */
export const DeploymentStatus = {
    PENDING: 'pending',
    SUCCESS: 'success',
    FAILED: 'failed',
    DELETING: 'deleting',
};
/**
 * Where a deployment came from — the origin-tracking vocabulary.
 *
 * A closed set with many authors. It lived in the API's config until
 * 2026-08-06, where being server-side made it unenforceable in the one
 * direction that matters — every client wrote a bare string, and a value
 * outside the set was **silently dropped** by the server, so a typo did not
 * fail anywhere. It stopped recording where deploys came from and said nothing.
 *
 * **The origin law: origin is declared by whatever we control — our code where
 * we ship code, our URL where we ship only a URL.** One rule decides every
 * member here and every future one:
 *
 * - **Where the platform ships CODE, the code declares it.** `web`, `sdk`,
 *   `cli`, `git`, `n8n`, `vsc` and `gmn` are surfaces this platform authors,
 *   so each names itself in its own source and nothing external is needed to
 *   tell them apart. A first-party WRAPPER counts as code even when it is a
 *   manifest: the GitHub Action and the Gemini extension each compose an
 *   invocation of a platform executable and relabel it through
 *   {@link SHIP_VIA_ENV}.
 *
 * **And only a surface that DEPLOYS gets a member — `@shipstatic/drop` is the
 * recorded refusal.** The widget collects and validates files and hands raw
 * `File[]` to its embedder, whose own SDK call is the deploy; drop makes no
 * upload call anywhere, so a `drp` member would be a word nothing can emit.
 * Those deploys read `sdk`, which is the truth: the embedder is a program
 * embedding the SDK directly. Mint the member the day drop grows an upload
 * arm, not before.
 * - **Where the platform ships only a URL, the URL declares it.** A
 *   marketplace listing runs somebody else's client against a bare endpoint —
 *   every one of them the same server speaking the same protocol, and
 *   indistinguishable in a request. The only thing such a listing's traffic
 *   has in common is the URL its users were handed, so the hosted MCP serves
 *   one DOOR per listing and the door's path IS the value: `gpt`, `cld`,
 *   `crs`.
 *
 * **A member names the most specific surface the platform can honestly
 * claim**, which is what makes the two FALLBACKS fallbacks rather than peers
 * of the named surfaces. `mcp` is any MCP host that was never handed a door of
 * its own; `api` is a call that reached the REST API naming nothing at all.
 * Guessing past either would be inventing attribution rather than recording
 * it, which is the one thing this vocabulary exists to prevent.
 *
 * Every member is three lowercase characters — the property that lets a
 * channel door's path and its attribution be spelled the same. The suite pins
 * both the width and the channel members by name.
 */
export const DeploymentVia = {
    /** The web dashboard. */
    WEB: 'web',
    /** A program embedding the SDK directly. */
    SDK: 'sdk',
    /** The `ship` CLI. */
    CLI: 'cli',
    /** Any MCP host with no door of its own — the stdio server included. The family fallback. */
    MCP: 'mcp',
    /** The GitHub Action. */
    GIT: 'git',
    /** The n8n community node. */
    N8N: 'n8n',
    /** Channel: the ChatGPT plugin → `mcp.<domain>/gpt`. */
    GPT: 'gpt',
    /** The VS Code extension. */
    VSC: 'vsc',
    /** Channel: the Claude connectors directory listing → `mcp.<domain>/cld`. */
    CLD: 'cld',
    /** Channel: the Cursor marketplace listing → `mcp.<domain>/crs`. */
    CRS: 'crs',
    /** The Gemini CLI extension. */
    GMN: 'gmn',
    /**
     * A deploy that reached the REST API naming no origin at all — the
     * platform-wide fallback, one altitude below `mcp`'s family fallback.
     *
     * **The API stamps it, since 2026-08-15.** A deploy that names no origin —
     * or names one this vocabulary does not know — is stored as `api`, so a
     * stored `null` now means only that the row predates attribution.
     *
     * It was declared one wave ahead of that decision, deliberately: vocabulary
     * must exist before a consumer can adopt it, and adding a member costs a
     * full constellation convoy, so the word shipped first and the server
     * adopted it with no convoy standing between the decision and the deploy.
     */
    API: 'api',
};
// =============================================================================
// DOMAIN TYPES
// =============================================================================
/**
 * A domain's STANDING: the one derived word, and what it asks of the owner.
 *
 * Not a DNS state and not a lifecycle. It answers the only question a reader
 * of a domain actually has, which is "what, if anything, do I have to do about
 * this one":
 *
 * - `live`: it serves the linked deployment. Nothing to do. Open it.
 * - `unlinked`: DNS is verified and nothing is published there, so a visitor
 *   meets the platform's reserved page. Link a deployment.
 * - `unverified`: the required DNS records are not all in place yet, in full
 *   or in part. Configure DNS.
 * - `paused`: the plan no longer has room for it. DNS is intact and the
 *   platform has stopped serving it; nothing DNS-shaped will help.
 *
 * **Derived, with exactly one owner: `domainStatus(row)` in the platform's
 * `shared/db/domains.ts`, beside `domainMapping(row)`.** The precedence is
 * policy rather than arithmetic, and it is stated here ONCE so that no
 * consumer has to restate it:
 *
 * ```
 * paused !== null               -> 'paused'
 * verification !== 'verified'   -> 'unverified'   (DNS comes first)
 * deployment === null           -> 'unlinked'
 * otherwise                     -> 'live'
 * ```
 *
 * This constant is the PUBLISHED RECORD of that rule, not a second
 * implementation of it. A consumer reads the word off the wire and switches
 * on it; a consumer that recomputes it from `deployment` and `verification`
 * has become the fourth copy of a precedence that drifts, which is exactly
 * how an unlinked domain wore a green "success" dot for months.
 *
 * The raw facts are still on the row, each said once, for a reader who wants
 * one of them specifically: {@link Domain.verification}, {@link Domain.verified},
 * {@link Domain.deployment}, {@link Domain.paused}.
 *
 * Note this is a repurposing of a wire name, which the API compatibility law
 * otherwise forbids: until types 3.0.0 `status` carried the DNS enum
 * (`pending`, `partial`, `success`) with `paused` glued on at the API's
 * projection. The exception was granted deliberately on 2026-09-20, on the
 * grounds that `status` is the right name for this and a `standing` beside a
 * deprecated `status` would preserve the half-design forever. It is recorded
 * beside the law it bends and is not precedent.
 */
export const DomainStatus = {
    LIVE: 'live',
    UNLINKED: 'unlinked',
    UNVERIFIED: 'unverified',
    PAUSED: 'paused',
};
/**
 * The DNS fact: how far the platform's one verifying act has got.
 *
 * - `pending`: the site's CNAME does not point at the platform.
 * - `partial`: it does, and the rest does not. Only a `www` domain has a
 *   rest, the apex A record, which serves nothing but the redirect to `www`.
 * - `verified`: every required record does. Platform names are born here,
 *   having no records to configure.
 *
 * **The CNAME divides the three because it is the record that serves the
 * site.** `partial` says the site is served and only the redirect is missing;
 * an apex A record without the CNAME serves nothing, so it is `pending`. The
 * verifier's drift line depends on exactly that: on a domain that has verified
 * before, a move into `pending` is the site no longer being served, and it is
 * the drift the operator is told about (`cloudflare/consumer`'s
 * `verifyDnsMapping`).
 *
 * Written by the DNS verifier and at birth, and by nothing else. **It is the
 * only field that says whether DNS is verified NOW**: its sibling instant
 * {@link Domain.verified} is monotonic and answers when it last was. A reader
 * who only wants to know whether to go and fix DNS reads {@link Domain.status};
 * this field is the diagnostic underneath it, and it is the ONLY place
 * `partial` is distinguishable from `pending`. Do not fold that distinction
 * into `status`: the standing says what to do, and "configure DNS" is the
 * same instruction either way.
 *
 * **On the noun, decided twice (2026-09-20), with the refusals recorded here
 * because an unrecorded one gets re-proposed forever.** `records` was the
 * first choice and is refused: on this same resource it would be a state word
 * on {@link Domain} and an array of DNS records on `DomainRecordsResponse`,
 * and the audience reading both schemas is largely agents. `dns` is refused
 * because it is taken twice over, by the provider lookup on
 * `GET /domains/:domain/dns` and by the column of that name. `verification`
 * is the platform's own family noun, shared by the `verify_dns` job, the
 * `POST /domains/:domain/verify` route, the {@link Domain.verified} instant
 * and the {@link Domain.verifications} count, so the three read as one act.
 * Its near-twin with `verifications` survives only in prose: in code the
 * compiler separates a string from a number.
 */
export const DomainVerification = {
    PENDING: 'pending',
    PARTIAL: 'partial',
    VERIFIED: 'verified',
};
/**
 * The anchor of the troubleshooting section on a domain's setup instructions
 * page, so a link can open the page there: `${share.url}#troubleshooting`.
 *
 * The API's instructions template gives the section this id and the console's
 * connect page links to it: two repos, one spelling, which is why it lives
 * here. The section is written for the domain's own DNS provider, which is why
 * the console sends a stuck reader to it rather than to the general docs. If
 * the spellings ever diverged, nothing would fail: the link would open the page
 * at its top and nobody would be told, the silent-drift class this
 * constitution exists to delete.
 */
export const SETUP_TROUBLESHOOTING_ANCHOR = 'troubleshooting';
/**
 * The envelope an `Idempotency-Key` must fit, and how long a replay lasts.
 *
 * Format lives here rather than on the server alone by the format-vs-policy
 * rule: a client can decide offline whether a key is well-formed, and the
 * API would reject the same value the same way.
 */
export const IDEMPOTENCY_KEY_CONSTRAINTS = {
    /**
     * HTTP header name. Here for the same reason {@link CALLER.HEADER} is: a
     * wire header has two ends, and the package that owns the value's format
     * is the only place both ends can read its name from.
     */
    HEADER: 'Idempotency-Key',
    MAX_LENGTH: 256,
    /** How long a stored 201 stays replayable. */
    WINDOW_SECONDS: 24 * 60 * 60,
};
/**
 * The most one deploy request may carry, and the framing a client counts
 * toward it. A deployment larger than one request arrives in several: a
 * declaration with its {@link DEPLOY_FIELDS.MANIFEST}, then
 * {@link API_PATHS.DEPLOYMENT_FILES} for the rest.
 *
 * Framing, not plan policy, which is why it is static rather than delivered
 * by `/limits`: a client splitting at a smaller number is always correct.
 *
 * **`MAX_BYTES` is a one-way door.** This package is bundled into every
 * published client, so an installed client keeps its cap forever. Raising it
 * is free (an older client splits smaller, which is always valid); lowering
 * it turns every installed client's largest request into a refusal.
 *
 * 64 MiB because it holds the largest file any plan allows (50 MiB) with its
 * framing, keeps a request inside the API's memory, finishes within the
 * SDK's request timeout at 2.5 Mbit/s, and sits under the edge's 100 MiB
 * request limit. Measured on the development environment at the boundary
 * (2026-10-18), a request of exactly 64 MiB, one 50 MiB file and 14 MiB of
 * others, stored in under half a second of CPU, sent with or without a
 * `Content-Length`, alone or two at once. What a request costs when its
 * files are rewritten is the API's to bound, and it does so per file.
 */
export const DEPLOY_REQUEST_CONSTRAINTS = {
    /** The most one deploy request may carry, as `Content-Length` counts it. */
    MAX_BYTES: 64 * 1024 * 1024,
    /**
     * The most multipart framing one part adds beyond its bytes and its name,
     * and the body's closing delimiter at most the same. A ceiling by the
     * multipart standard (a boundary is at most 70 characters), not a
     * measurement.
     */
    PART_OVERHEAD: 256,
};
/**
 * Normalize a `via` value from any transport — trimmed, lowercased, and a
 * member of {@link DeploymentVia}, or `undefined`.
 *
 * A format rule by this package's own test: a client can decide offline
 * whether a value is well-formed, and the API reaches the same verdict on the
 * same input. It lived server-side until 2026-08-06, which meant clients could
 * only learn their label was unusable by noticing analytics had gone quiet.
 *
 * **Not knowing your `via` is not an error** — an unrecognized value yields
 * `undefined` rather than throwing, because origin tracking is telemetry and a
 * deploy must never fail over it. A caller that has an honest default should
 * prefer it (`normalizeVia(process.env.SHIP_VIA) ?? DeploymentVia.CLI`): the
 * deploy really did come from the CLI, so recording that beats recording
 * nothing.
 */
export function normalizeVia(value) {
    if (!value || typeof value !== 'string')
        return undefined;
    const via = value.trim().toLowerCase();
    return Object.values(DeploymentVia).includes(via)
        ? via
        : undefined;
}
/**
 * Validate an idempotency key, returning the trimmed value or `undefined`
 * when none was supplied. Throws {@link ShipError.validation} when the value
 * cannot be sent — the same verdict the API would reach, reached earlier.
 */
export function validateIdempotencyKey(value) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== 'string') {
        throw ShipError.validation('Idempotency key must be a string.');
    }
    const key = value.trim();
    if (!key) {
        throw ShipError.validation('Idempotency key must not be empty.');
    }
    if (key.length > IDEMPOTENCY_KEY_CONSTRAINTS.MAX_LENGTH) {
        throw ShipError.validation(`Idempotency key must be at most ${IDEMPOTENCY_KEY_CONSTRAINTS.MAX_LENGTH} characters.`);
    }
    return key;
}
// =============================================================================
// ACCOUNT TYPES
// =============================================================================
/**
 * Every plan an account can hold — the platform's whole plan vocabulary, in
 * one place, and nothing about what a plan is WORTH.
 *
 * A plan is a TIER and nothing else. Whether an account may act is a separate
 * fact (`Account.suspended`; deletion ends the session outright), so an
 * account keeps its tier through suspension and into deletion.
 *
 * - **Free** — `free`.
 * - **Billed** — `pro`, `team`. The plans a customer buys; the only plans
 *   Stripe knows about, and the only ones the platform never sets by hand —
 *   each is derived from the Stripe Subscription, which names its plan on the
 *   Price it is on. They form a ladder: a dearer tier is a superset of the one
 *   below it, and the API says which is next in {@link Account.upgrade}.
 * - **Granted** — `scale`, `sponsored`. Paid plans the operator confers by
 *   hand, never reached through Checkout: no Stripe subscription and no
 *   Stripe object behind them. These and `free` are the only plans an
 *   operator can set; a billed plan is only ever Stripe's to confer.
 *
 * The numbers each plan confers — caps, sizes — are POLICY and are delivered
 * by the API (`GET /plans`, `GET /account`, `GET /limits`), never published
 * here: a price or a cap in a published package is pinned to whatever version
 * a client installed (`CLAUDE.md`, "Validation: format vs policy").
 */
export const AccountPlan = {
    FREE: 'free',
    PRO: 'pro',
    TEAM: 'team',
    SCALE: 'scale',
    SPONSORED: 'sponsored',
};
/** The member places an account has taken: its members, and its pending invitations. */
export function placesTaken(usage) {
    return usage.members + usage.invitations;
}
/**
 * The kind each count of {@link AccountUsage} names, as a person reads it:
 * one and many. The one vocabulary every surface that says a count uses, the
 * api's cap refusals and the console's room notes alike, so "custom domain"
 * is one word wherever it is met. A person reads "ShipStatic domain" where
 * the code says platform domain; the code's word never reaches them.
 */
export const USAGE_KIND = {
    deployments: { one: 'deployment', many: 'deployments' },
    platformDomains: { one: 'ShipStatic domain', many: 'ShipStatic domains' },
    customDomains: { one: 'custom domain', many: 'custom domains' },
    members: { one: 'member', many: 'members' },
    invitations: { one: 'invitation', many: 'invitations' },
};
/**
 * What an account holds beyond a set of caps, per kind, only the kinds that
 * are over: what a move down a tier must shed before it is booked, and what
 * the console notes on a cheaper card.
 *
 * Member places are read against `caps.members`: the places over the cap are
 * counted once ({@link placesTaken} less the cap), members are named first,
 * because a member over the cap has to go whatever else the account holds,
 * and what is over after them is the invitations to cancel. So two members
 * and one invitation against a cap of one is one member and one invitation,
 * and five members against the same cap is four members and no invitation.
 * The two domain kinds are read against their own caps. Deployments are not
 * read at all: nothing pauses them, and above their cap they only block
 * creation (see {@link Caps}).
 */
export function excessOver(usage, caps) {
    const places = Math.max(0, placesTaken(usage) - caps.members);
    const members = Math.max(0, usage.members - caps.members);
    const over = {
        members,
        invitations: places - members,
        customDomains: usage.customDomains - caps.customDomains,
        platformDomains: usage.platformDomains - caps.platformDomains,
    };
    return Object.fromEntries(Object.entries(over).filter(([, count]) => count > 0));
}
/**
 * Counts of kinds in words, people first: "4 members and 2 custom domains",
 * or nothing. The order is {@link USAGE_KIND}'s minus the two kinds a count never
 * names here, deployments and invitations, which have their own sentence
 * ({@link fitPhrase}).
 */
export function countPhrase(excess) {
    return listOf(['members', 'customDomains', 'platformDomains']
        .filter((kind) => (excess[kind] ?? 0) > 0)
        .map((kind) => counted(kind, excess[kind] ?? 0)));
}
/**
 * What to do before an account fits a set of caps, in words: "remove 1
 * member and cancel 1 invitation", "remove 2 custom domains", "cancel 2
 * invitations", or nothing when it fits. Two verbs, because a member place is
 * freed two ways: a member is removed, a pending invitation is cancelled. The
 * api's change door and the console's room notes say this one phrase, so a
 * refusal and the note that preceded it cannot differ.
 */
export function fitPhrase(excess) {
    const remove = countPhrase(excess);
    const invitations = excess.invitations ?? 0;
    return listOf([
        ...(remove ? [`remove ${remove}`] : []),
        ...(invitations > 0 ? [`cancel ${counted('invitations', invitations)}`] : []),
    ]);
}
/** "4 members", "1 custom domain". */
const counted = (kind, count) => `${count} ${count === 1 ? USAGE_KIND[kind].one : USAGE_KIND[kind].many}`;
/** "a", "a and b", "a, b and c". */
const listOf = (parts) => parts.length > 1
    ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
    : (parts[0] ?? '');
/**
 * The word a refusal about the account carries in `details.account`, so a
 * client can tell the two apart without reading the sentence:
 *
 * - `paused`: a 403. The person is a member, and the account holds more
 *   members than its plan allows (see {@link AccountAccess}).
 * - `unknown`: a 404. The account the request names ({@link ACCOUNT_HEADER})
 *   is one the person is not a member of, or one that does not exist; the
 *   two are indistinguishable by design. A tab whose person was removed from
 *   the account meets it on its next request.
 */
export const AccountRefusal = {
    PAUSED: 'paused',
    UNKNOWN: 'unknown',
};
// =============================================================================
// PERSON NAME
// =============================================================================
/**
 * The name a person is ADDRESSED by: their name, else their address. A
 * person always has an email and only sometimes a name (a magic-link signup
 * has none), so every surface that addresses one needs the same fallback,
 * and it lives here because two independent holders applied it, the API in
 * its letters and pulses and the console in its labels, with nothing but
 * their docblocks holding the copies together. The console cannot avoid
 * deriving it: a roster's rows come from the identity provider's own member
 * list, not from this wire.
 *
 * Coalesced with `||` rather than `??` deliberately: a provider that hands
 * back an empty or whitespace-only name is a name nobody can be called by.
 *
 * This is the ADDRESSING register. A surface that STATES the name as a fact
 * (an operator table, a CLI printout, a machine payload) shows it as stored
 * and marks absence its own way, and does not call this.
 */
export function personName(person) {
    return person.name?.trim() || person.email;
}
/**
 * The short form, where the room is narrow: the first word of the name, else
 * the address. The whole address rather than its local part, since a
 * truncated address still reads as an address while a bare local part reads
 * as a username the platform does not have. The sidebar, a greeting, and the
 * name an account is born with all read this.
 */
export function personShortName(person) {
    return person.name?.trim().split(' ')[0] || person.email;
}
// =============================================================================
// WIRE SURFACE
// =============================================================================
/**
 * Every path the public API answers on, declared once.
 *
 * The URL surface was written out in four places — the API's mounts, the
 * SDK's client, the dashboard's client, and the post-deploy smoke — so a
 * rename meant finding all four. The first three now read this table.
 *
 * The smoke (`cloudflare/api/smoke.mjs`) deliberately still spells its own:
 * five of its nine paths are `/admin/*`, which this table excludes by
 * design, and splitting one list between a registry and literals reads worse
 * than keeping it uniform.
 *
 * **What this guarantees, exactly.** Collection paths are mounted from here,
 * so producer and consumer cannot diverge. Item paths are declared here and
 * consumed by clients, but the API spells them relative to their mount
 * (`/:deployment/config`), so the table does not *generate* them — it is
 * held to them by `api/tests/architecture/api-paths.test.ts`, which fails if
 * any entry names a path no route answers. Some entries have no client yet
 * (`DEPLOYMENT_CONFIG`, `DOMAIN_PROPAGATION` — endpoints the SDK
 * deliberately does not reach); the fence is what keeps those honest rather
 * than merely asserted.
 *
 * **The operator surface is deliberately absent.** `/admin/*` paths belong
 * to `web/my`, for the same reason its row types do: this package is
 * published, and the operator surface is not public (see `CLAUDE.md`, "Admin
 * types"). A path here is a promise to every npm consumer; `/admin` is a
 * promise to one dashboard.
 *
 * Item paths are functions rather than templates so the key is interpolated
 * in one place, encoded the same way by every caller.
 */
export const API_PATHS = {
    DEPLOYMENTS: '/deployments',
    DEPLOYMENT: (deployment) => `/deployments/${deployment}`,
    DEPLOYMENT_CONFIG: (deployment) => `/deployments/${deployment}/config`,
    /** Fills a declared deployment with more of its files. */
    DEPLOYMENT_FILES: (deployment) => `/deployments/${deployment}/files`,
    DOMAINS: '/domains',
    DOMAIN: (domain) => `/domains/${domain}`,
    DOMAIN_VERIFY: (domain) => `/domains/${domain}/verify`,
    DOMAIN_DNS: (domain) => `/domains/${domain}/dns`,
    DOMAIN_RECORDS: (domain) => `/domains/${domain}/records`,
    DOMAIN_SHARE: (domain) => `/domains/${domain}/share`,
    DOMAIN_PROPAGATION: (domain) => `/domains/${domain}/propagation`,
    DOMAINS_VALIDATE: '/domains/validate',
    TOKENS: '/tokens',
    TOKEN: (token) => `/tokens/${token}`,
    ACCOUNT: '/account',
    ACCOUNT_KEY: '/account/key',
    ACCOUNT_CLAIM: '/account/claim',
    ACTIVITIES: '/activities',
    LABELS: '/labels',
    LIMITS: '/limits',
    PLANS: '/plans',
    PING: '/ping',
    SETUP: '/setup',
    /**
     * The single-page question, asked without deploying. A deploy that sends
     * {@link DEPLOY_FIELDS.SPA} `true` is asked it at its declaration, so
     * a client never needs this path to get the routing config: it answers the
     * clients that predate the field, which ask here and write the config
     * themselves.
     */
    SPA_CHECK: '/spa-check',
    UPLOAD: '/upload',
};
/**
 * The deploy request's multipart field names: the other half of the wire
 * surface beside {@link API_PATHS}. These are the names the API reads from a
 * multipart body on `POST /deployments` and the first-party `/upload`.
 *
 * Declared once because the body has three independent WRITERS — the SDK's
 * Node and browser body builders, and the n8n community node's hand-rolled
 * client (which cannot import this under n8n Cloud's zero-dependency rule,
 * and fences its restated copy instead) — and until this export every writer
 * restated the strings the API parses, with nothing comparing them.
 *
 * `FILES` carries one entry per file (the API reads it with `getAll`); every
 * other field is single. Flags are serialized as the literal string
 * `'true'`; the `@internal` ones belong to first-party surfaces only.
 */
export const DEPLOY_FIELDS = {
    /** One entry per file — read with `getAll`. */
    FILES: 'files[]',
    /**
     * JSON array of {@link ManifestEntry}, one per file of the deployment: its
     * declaration. Optional; a request without one is the whole deployment.
     */
    MANIFEST: 'manifest',
    /** JSON array of label strings. */
    LABELS: 'labels',
    /** The deploying surface's {@link DeploymentVia} member. */
    VIA: 'via',
    /** Plaintext password — the API hashes it server-side. */
    PASSWORD: 'password',
    /**
     * Requested lifetime in SECONDS — a duration, never an instant. The API
     * computes and stores the expiry, so the wire carries no client clock.
     * See {@link validateTtl}.
     */
    TTL: 'ttl',
    /** @internal Server-processing flag — first-party `/upload` only. */
    BUILD: 'build',
    /** @internal Server-processing flag — first-party `/upload` only. */
    PRERENDER: 'prerender',
    /**
     * Whether the platform asks the single-page question of the deployment's
     * files: `true` asks, and `false` or absence does not, so a request that
     * names nothing is never given a config it did not ask for. Asked, when the
     * root `index.html` is an empty application shell and no `ship.json` is
     * shipped, the platform adds the routing config that serves every path from
     * `index.html`. The question is asked of the `index.html` the asking request
     * carries. A client sends `true` unless
     * {@link DeploymentUploadOptions.spaDetect} is `false`: the option and the
     * field are two names for two layers. The name and its `true` are the ones
     * production's console has always sent; the field is read on both endpoints
     * and by a deploy split across several requests.
     */
    SPA: 'spa',
    /**
     * @internal The build settings, first-party `/upload` only and meaningful
     * only with `BUILD`: the command to run instead of the manifest's build
     * script, and the folder the site lands in. See {@link BUILD_SETTINGS}.
     */
    BUILD_COMMAND: 'buildCommand',
    /** @internal See {@link DEPLOY_FIELDS.BUILD_COMMAND}. */
    OUTPUT_DIR: 'outputDir',
    /** @internal reCAPTCHA proof — `web/www`'s public uploader only. */
    CAPTCHA: 'captcha',
};
/**
 * The JSON deploy body's file grammar: the OTHER body both deploy doors
 * accept, beside the multipart one {@link DEPLOY_FIELDS} names. A JSON caller
 * sends `{ files: [{ path, content, encoding? }] }`, and this is the whole
 * of what one entry may say: three field names, two encodings, one default.
 *
 * Declared once because the grammar has three independent holders that the
 * wire forces to restate it: the API's zod schema (the original), the hosted
 * MCP's tool input, and the n8n community node, which cannot import this
 * under n8n Cloud's zero-dependency rule and fences its copy against this
 * object instead. Each had its own literal table until 2.24.0; the last two
 * types convoys walked without minting the owner, which is exactly the drift
 * the No-Fourth-Category Law's deferral clause names.
 *
 * `content` is the file's bytes as text: raw for `utf-8` (the default, and
 * the right choice for HTML, CSS, JS, JSON and SVG), base64 for binary.
 */
export const DEPLOY_FILE_GRAMMAR = {
    /** Relative path within the site, no leading slash. */
    PATH: 'path',
    /** The file's bytes, as text in the entry's encoding. */
    CONTENT: 'content',
    /** Optional; one of {@link DEPLOY_FILE_GRAMMAR.ENCODINGS}. */
    ENCODING: 'encoding',
    /** What an entry that names no encoding means. */
    DEFAULT_ENCODING: 'utf-8',
    /** The closed set. `utf-8` is raw text; `base64` is for binary only. */
    ENCODINGS: ['utf-8', 'base64'],
};
// =============================================================================
// ERROR SYSTEM
// =============================================================================
/**
 * All possible error types in the ShipStatic platform.
 *
 * Developer-friendly key names map to stable wire-format string values.
 * Both the value and the type are exported under the same name so callers
 * can use `ErrorType.Validation` (value comparison) and `: ErrorType` (type
 * annotation) without ceremony — matching the pattern other status objects
 * (`DeploymentStatus`, `DomainStatus`, `AccountPlan`, `AuthMethod`) follow.
 */
export const ErrorType = {
    /**
     * Validation failed. Input shape is wrong.
     *
     * Carries 400 when an API judged it — including a client-side pre-check of a
     * rule the server enforces too, which keeps the error identical wherever it
     * was caught. **Statusless** when a client rejects something no API judges,
     * such as a CLI's own command grammar: `status` is documented "(API
     * contexts)" on `ErrorResponse`, so there is none to report.
     */
    Validation: 'validation_failed',
    /** Resource not found (404). */
    NotFound: 'not_found',
    /** Authenticated but not allowed (403). User lacks permission for this action. */
    Forbidden: 'forbidden',
    /** Rate limit exceeded (429). */
    RateLimit: 'rate_limit_exceeded',
    /** Authentication required or failed (401). Missing/invalid credentials. */
    Authentication: 'authentication_failed',
    /** Business rule violation. Catch-all for 4xx state-rule errors that aren't more specific. */
    Business: 'business_logic_error',
    /** API server error (500). Generic server-side fault. */
    Api: 'internal_server_error',
    /**
     * The platform is closed for maintenance (503). A deliberate operator
     * state, not a fault — nothing errored; the API is refusing work on
     * purpose, and deployed sites keep serving throughout.
     *
     * Distinct from `Api` at 503, which the platform already uses for a
     * dependency that failed. A consumer has to tell
     * "we closed the door" from "something broke": the two get opposite words
     * and opposite retry behaviour.
     */
    Maintenance: 'maintenance',
    /**
     * The project could not be built as submitted (422). The platform ran the
     * caller's own instructions, the dependency install and then the build
     * script, and they failed. So the verdict is the caller's: deterministic,
     * not worth a retry, and answered with the evidence. `details` is a
     * `BuildFailureDetails`, and the message is the one sentence the builder
     * authored for the person who will fix the project.
     *
     * Distinct from `Api` at 500, which is what the platform answers when it
     * could not RUN the build at all (the sandbox failed to start, a host
     * fault). A consumer has to tell "your project cannot be built" from "we
     * could not build it": the two get opposite words, opposite retry
     * behaviour, and only the second pages an operator.
     */
    Build: 'build_failed',
    /** Network/connection error. Client-side only — set by HTTP clients on fetch failure; never produced server-side. */
    Network: 'network_error',
    /**
     * A deadline expired before the exchange completed. Client-side only — set
     * by HTTP clients when a timeout signal fires; never produced server-side.
     *
     * A member of the NETWORK category rather than a sibling of it:
     * `isNetworkError()` answers "nothing was exchanged", which is true of a
     * deadline exactly as it is of a refused connection, so every consumer that
     * retries, declines to report, or declines to relay a wire message on that
     * category is already right about a timeout. The distinct TYPE exists for
     * the one decision the category cannot make — what to SAY. "Check your
     * internet connection" is the wrong sentence for a five-minute deploy
     * ceiling, and a surface can only tell the two apart by type.
     *
     * The same relationship every comparable SDK ships:
     * `APIConnectionTimeoutError extends APIConnectionError`.
     */
    Timeout: 'timeout_error',
    /** Operation was cancelled. Client-side only — set on `AbortSignal` abort; never produced server-side. */
    Cancelled: 'operation_cancelled',
    /** File operation error. Client-side only — set by SDK during local file processing; never produced server-side. */
    File: 'file_error',
    /** Configuration error. Client-side only — set by SDK during config parsing/validation; never produced server-side. */
    Config: 'config_error',
};
/**
 * Error types that originate exclusively on the client (HTTP clients, SDK
 * file processing, local config parsing). These never appear on the wire
 * from the server, so `fromHttpResponse` will not trust them even if a
 * misbehaving server claims one in `body.error`.
 */
const CLIENT_ONLY_ERROR_TYPES = new Set([
    ErrorType.Network,
    ErrorType.Timeout,
    ErrorType.Cancelled,
    ErrorType.File,
    ErrorType.Config,
]);
/**
 * Categorizes error types for the `isClientError` / `isNetworkError` /
 * `isAuthError` helpers. Each `Set` is typed against the wider `ErrorType`
 * union so `.has(error.type)` accepts any value from the union.
 */
const ERROR_CATEGORIES = {
    /**
     * Client-attributable types. Exhaustive over the 4xx-carrying types, and
     * over the statusless ones too — those are raised locally and have no
     * status for `isClientError`'s second arm to read, so omitting one makes it
     * read as a server fault. The rule is the membership test: every type in
     * `CLIENT_ONLY_ERROR_TYPES` except the two `isNetworkError` owns belongs
     * here.
     *
     * `Cancelled` was missing until 2026-07-29, which is exactly that failure:
     * a caller who aborted their own deploy was told "server error: please try
     * again" — the CLI's fallback for everything this set does not claim.
     *
     * `Timeout` is deliberately NOT here, and it is the sharper case, because
     * it is the one client-only type that is not the client's fault: the
     * caller set a ceiling, but what exhausted it was the network or the
     * server. Reading it as client-attributable would say the caller erred,
     * and it would silently disarm every consumer whose retry predicate
     * declines `isClientError()` — a deadline is precisely the failure worth
     * a second attempt.
     */
    client: new Set([
        ErrorType.Build,
        ErrorType.Business,
        ErrorType.Cancelled,
        ErrorType.Config,
        ErrorType.File,
        ErrorType.Forbidden,
        ErrorType.NotFound,
        ErrorType.RateLimit,
        ErrorType.Validation,
    ]),
    /**
     * The exchange never happened. Two types, one category: a refused
     * connection and an expired deadline differ in what a surface should SAY
     * and in nothing else a consumer decides on — both are retryable, neither
     * carries a wire message to relay, neither is worth reporting as an
     * incident. See `ErrorType.Timeout` for why the type is distinct anyway.
     */
    network: new Set([ErrorType.Network, ErrorType.Timeout]),
    auth: new Set([ErrorType.Authentication]),
};
/**
 * Error types the server can legitimately produce on the wire. Used by
 * `ShipError.fromHttpResponse` to validate the body's `error` field before
 * trusting it as `ShipError.type`. Derived by exclusion from
 * `CLIENT_ONLY_ERROR_TYPES` so adding a new server-producible type to
 * `ErrorType` is automatically picked up.
 */
const SERVER_PRODUCIBLE_ERROR_TYPES = new Set(Object.values(ErrorType).filter((t) => !CLIENT_ONLY_ERROR_TYPES.has(t)));
/**
 * Ceiling on a message adopted from a **non-JSON** error body — a foreign
 * responder's, never this platform's. Generous for the plain-text one-liners
 * intermediaries actually send (`error code: 1015`), far below a document.
 * Our own messages are never measured against it: a JSON body is the API's
 * contract, and truncating a long validation message would be the bug.
 */
const MAX_FOREIGN_MESSAGE_LENGTH = 200;
/**
 * Did the runtime say the exchange never completed?
 *
 * Clients branch on the TYPE, never on message strings, so a misclassified
 * transport failure is a lie every consumer inherits — and the one that costs
 * most: `Api` claims a server answered when nothing was exchanged, and a
 * retrying caller will not retry it.
 *
 * **Every row below is a transcript, not a belief.** Captured 2026-08-12
 * against real runtimes — Node and Bun by direct run, the three engines by a
 * one-off playwright probe, workerd through miniflare. The capture scripts are
 * in `tests/errors.test.ts`, "runtime failure shapes".
 *
 * | runtime          | connection refused / DNS failure                     | malformed URL                                    |
 * |------------------|------------------------------------------------------|--------------------------------------------------|
 * | Node 22 / undici | `TypeError: fetch failed`                             | `TypeError: Failed to parse URL from …`          |
 * | Bun 1.3.14       | `Error` `code:'ConnectionRefused'`                    | `TypeError` `code:'ERR_INVALID_URL'`             |
 * | Chromium 151     | `TypeError: Failed to fetch`                          | `TypeError: …Failed to parse URL from …`         |
 * | Firefox 153      | `TypeError: NetworkError when attempting to fetch …`  | `TypeError: … is not a valid URL.`               |
 * | WebKit 26.5      | `TypeError: Load failed`                              | `TypeError: URL is not valid or contains user …` |
 * | workerd          | `Error: Network connection lost.` (DNS: `internal error; reference = …`) | `TypeError: Invalid URL: …`   |
 *
 * Reading that table gives the rule, and it is the INVERSE of the obvious one.
 * The transport class is unbounded — every OS, TLS and DNS failure any engine
 * will ever name — while the class fetch raises for its own ARGUMENTS is
 * small, and every runtime names the URL when it complains about one. So the
 * bounded side is the one worth testing, and the residual risk points the safe
 * way: an unrecognised sentence lands on `Network`, which says only that
 * nothing was exchanged.
 *
 * That inversion is what fixes **WebKit**, whose `Load failed` carries no code
 * and no "fetch", and which every browser-SDK and `@shipstatic/drop` user on
 * Safari was hitting as `Api`. It also makes the six runtimes AGREE about a
 * malformed URL, which they did not before: the previous rule tested the
 * message for "fetch", and Chromium's and Firefox's URL complaints both
 * contain it, so the same mistake was `Network` on three engines and `Api` on
 * three.
 *
 * **workerd is the recorded gap.** It rejects with a plain `Error`, no code
 * and no shared sentence — and its two failure modes produce two unrelated
 * ones — so nothing here can classify it and it lands on `Api`. Left alone
 * rather than patched with a dialect string: the one consumer running ship in
 * that runtime (`cloudflare/mcp`) reaches the API through a service BINDING,
 * which is in-process and does not produce transport rejections at all.
 *
 * **The `TokenProvider` case stopped being a trade when clients gained
 * retries.** A caller's provider that throws a coded error is typed `Network`
 * here, which was recorded as "both are wrong for it; `Network` is the cheaper
 * wrong" — written when the classification decided only what a surface would
 * SAY. It now also decides whether the call is retried, and that turns the
 * cheaper wrong into the right answer: a `TokenProvider` is where minting and
 * refresh live, so the common one is an OAuth refresh over the network, and a
 * transient failure there is precisely what another attempt repairs.
 *
 * The residual cost is a deterministic provider fault — a genuinely missing
 * keychain entry — invoking the provider three times over a few hundred
 * milliseconds before failing with the same error. No request leaves the
 * process on any of them. That is the cheap direction of a bet whose other
 * side is a refused deploy, and suppressing it would need a way to mark
 * credential faults non-retryable: machinery with one holder, refused by the
 * estate's stopping rule. Provider failures that carry no code are `Api` and
 * are not retried at all, and a provider yielding nothing is `Authentication`
 * by the fail-closed invariant, which is likewise terminal.
 */
function isTransportFailure(cause) {
    const code = cause.code;
    // Bun is the one runtime that puts a CODE on an argument error, so it is
    // excluded before the code arm can claim it.
    if (code === 'ERR_INVALID_URL')
        return false;
    // A string `code` is a runtime naming a transport-level failure. An
    // allowlist of codes was written first and rejected — the TLS row alone
    // would mean enumerating BoringSSL's certificate table, and a code nobody
    // guessed is precisely the bug this closes. A `DOMException`'s code is a
    // NUMBER, so aborts and timeouts never reach here.
    if (typeof code === 'string')
        return true;
    // WHATWG has fetch reject with a TypeError for BOTH halves — network error
    // and argument error — so among TypeErrors the URL is the discriminator.
    if (cause instanceof TypeError)
        return !/\burl\b/i.test(cause.message);
    // Anything else — an ordinary JS fault, or workerd — is not evidence.
    return false;
}
/**
 * Simple unified error class for both API and SDK
 */
export class ShipError extends Error {
    type;
    status;
    details;
    constructor(type, message, status, details) {
        super(message);
        this.type = type;
        this.status = status;
        this.details = details;
        this.name = 'ShipError';
    }
    /** Convert to wire format */
    toResponse() {
        // Strip authentication details when they carry an `internal` telemetry
        // tag (see `ShipError.authentication` JSDoc) — these are server-side
        // diagnostics like 'session_invalid' that must not leak to clients.
        const authDetails = this.details;
        const details = this.type === ErrorType.Authentication && authDetails?.internal ? undefined : this.details;
        return {
            error: this.type,
            message: this.message,
            status: this.status,
            details,
        };
    }
    /**
     * Construct a `ShipError` from an HTTP error response.
     *
     * Best-effort body parse for `{ message, error?, details? }`. Message
     * resolution: `body.message` → `body.error` → `"<operationName> failed with
     * status <N>"`.
     *
     * Type resolution: trusts `body.error` when it's a known server-producible
     * `ErrorType` (preserves the wire's intent — server's
     * `ShipError.validation(...)` round-trips back to `ErrorType.Validation`
     * on the client). Falls back to status-derived (401 → Authentication,
     * 403 → Forbidden, 429 → RateLimit, else → Api) for non-API responses
     * (CDN errors, intermediaries) or malformed bodies. Client-only types
     * (`Network`, `Timeout`, `Cancelled`, `File`, `Config`) are filtered out of the
     * trusted set — a misbehaving server claiming one of those is ignored.
     *
     * `operationName` (e.g. `"Get account"`) is used to compose the fallback
     * message. Defaults to `"Request"`. Same convention as `fromFetchError`.
     *
     * Async because it reads the response body. Returns rather than throws so
     * callers can compose; most will `throw await ShipError.fromHttpResponse(...)`.
     */
    static async fromHttpResponse(response, operationName) {
        let message;
        let details;
        let bodyType;
        try {
            const contentType = response.headers.get('content-type');
            if (contentType?.includes('application/json')) {
                const json = await response.json();
                if (json && typeof json === 'object') {
                    const obj = json;
                    if (typeof obj.message === 'string')
                        message = obj.message;
                    else if (typeof obj.error === 'string')
                        message = obj.error;
                    details = obj.details;
                    if (typeof obj.error === 'string' && SERVER_PRODUCIBLE_ERROR_TYPES.has(obj.error)) {
                        bodyType = obj.error;
                    }
                }
            }
            else {
                // A non-JSON body did not come from this platform — every API error
                // is `ErrorResponse` JSON — so it is an intermediary's output, and
                // the two kinds it produces need opposite treatment. A CDN's plain
                // `error code: 1015` is the most useful thing there is to say. A
                // proxy's HTML error page is a *document*, not a message: adopting it
                // verbatim made a misconfigured `apiUrl` print 2,059 characters of
                // markup as the error. Trust it only when it reads as a message.
                const text = (await response.text()).trim();
                if (text && !text.startsWith('<') && text.length <= MAX_FOREIGN_MESSAGE_LENGTH) {
                    message = text;
                }
            }
        }
        catch {
            // Body unreadable; fall through to operationName-derived message.
        }
        // Rate-limit (and 503) timing rides the `Retry-After` HEADER, which a
        // body-only reader would drop. Lift it into `details` as seconds so
        // consumers can back off from the typed error alone, without keeping the
        // raw Response around. Body-carried fields are preserved and win.
        const retryAfterHeader = response.headers.get('retry-after');
        if (retryAfterHeader !== null) {
            const value = retryAfterHeader.trim();
            const seconds = /^\d+$/.test(value)
                ? Number(value)
                : Math.ceil((Date.parse(value) - Date.now()) / 1000);
            if (Number.isFinite(seconds) && seconds >= 0) {
                const existing = details && typeof details === 'object' ? details : {};
                if (existing.retryAfter === undefined) {
                    details = { ...existing, retryAfter: seconds };
                }
            }
        }
        message = message || `${operationName || 'Request'} failed with status ${response.status}`;
        const type = bodyType ??
            (response.status === 401
                ? ErrorType.Authentication
                : response.status === 403
                    ? ErrorType.Forbidden
                    : response.status === 429
                        ? ErrorType.RateLimit
                        : ErrorType.Api);
        return new ShipError(type, message, response.status, details);
    }
    /**
     * Construct a `ShipError` from an error caught around a `fetch()` call.
     *
     * The mirror of `fromHttpResponse` for the *other* side of the HTTP error
     * story — the network layer failing (offline, CORS, abort) rather than the
     * server returning a non-OK response.
     *
     * Routing:
     * - Already a `ShipError` → returned as-is (caller's intent preserved)
     * - `AbortError` → `ShipError.cancelled(...)` — someone stopped it on purpose
     * - `TimeoutError` → `ShipError.timeout(...)` — a deadline expired; the
     *   message names the timeout, and the type is in the network CATEGORY
     *   because nothing was exchanged
     * - A transport failure → `ShipError.network(...)` — see `isTransportFailure`
     *   for what each runtime offers as evidence
     * - Any other `Error` → `ShipError(Api, ...)` (no HTTP status — fetch never reached the server)
     * - Anything else (string, undefined, etc.) → `ShipError(Api, ...)`
     *
     * **Abort and timeout are read from `name` BEFORE any `instanceof Error`
     * gate.** A `DOMException` satisfies that gate in every runtime measured
     * (Node, Bun, Chromium, Firefox, WebKit, workerd — all six), but the
     * inheritance is a comparatively recent spec change and this classification
     * has no reason to depend on it: `name` is where the meaning lives, and
     * reading it first costs nothing. The suite plants a non-`Error`
     * `DOMException` shape to hold the arm, since no runtime on the table
     * produces one.
     *
     * A caller's own `AbortSignal.timeout()` is the reachable source of
     * `TimeoutError` — and the two are NOT interchangeable per runtime: WebKit
     * reports a fired `AbortSignal.timeout()` as `AbortError`, so on Safari a
     * deadline is indistinguishable from a cancellation and lands on
     * `Cancelled`. Recorded rather than worked around; `Cancelled` is honest
     * there, since the caller's signal is what stopped it.
     *
     * The optional `operationName` is composed into the message for context:
     * `"Get account was cancelled"`, `"Get account failed: ..."`. Defaults to
     * `"Request"` when omitted.
     */
    static fromFetchError(cause, operationName) {
        if (isShipError(cause))
            return cause;
        const op = operationName || 'Request';
        // Read by NAME, ahead of the Error gate — see the note above.
        const name = cause?.name;
        if (name === 'AbortError') {
            return ShipError.cancelled(`${op} was cancelled`);
        }
        if (name === 'TimeoutError') {
            // A deadline: not a fault, not a cancellation, and — since it has its
            // own type — no longer merely "network". Nothing was exchanged, which
            // is what keeps it in the network CATEGORY and therefore retryable; the
            // type is what lets a surface say "timed out" instead of sending
            // someone to check their Wi-Fi. The runtime's own sentence is dropped
            // rather than relayed: "The operation was aborted due to timeout" is
            // the mechanism, not the news.
            return ShipError.timeout(`${op} timed out`, { cause });
        }
        if (cause instanceof Error) {
            if (isTransportFailure(cause)) {
                return ShipError.network(`${op} failed: ${cause.message}`, { cause });
            }
            return new ShipError(ErrorType.Api, `${op} failed: ${cause.message}`);
        }
        return new ShipError(ErrorType.Api, `${op} failed: Unknown error`);
    }
    // Factory methods. Uniform shape `(message, details?)` with two principled
    // exceptions: `notFound` composes its message from (resource, id?), and
    // `business` / `api` accept an optional status because they're the
    // multi-status fallbacks.
    static validation(message, details) {
        return new ShipError(ErrorType.Validation, message, 400, details);
    }
    static notFound(resource, id) {
        const message = id ? `${resource} ${id} not found` : `${resource} not found`;
        return new ShipError(ErrorType.NotFound, message, 404);
    }
    static forbidden(message, details) {
        return new ShipError(ErrorType.Forbidden, message, 403, details);
    }
    static rateLimit(message = 'Too many requests', details) {
        return new ShipError(ErrorType.RateLimit, message, 429, details);
    }
    /**
     * Construct an Authentication (401) error.
     *
     * **Telemetry pattern — `details: { internal: '<tag>' }`.** When the
     * server creates an auth error with an `internal` key in `details`
     * (e.g. `{ internal: 'session_invalid' }`), `toResponse()` strips the
     * entire `details` object before serialization. This keeps the wire
     * response a clean "Authentication failed" while preserving granular
     * server-side telemetry (which strategy/check failed) for logs and tests.
     *
     * Use this pattern in API auth code; do not put client-visible info under
     * `internal`. Other `details` keys round-trip normally.
     */
    static authentication(message = 'Authentication required', details) {
        return new ShipError(ErrorType.Authentication, message, 401, details);
    }
    static business(message, status = 400, details) {
        return new ShipError(ErrorType.Business, message, status, details);
    }
    static network(message, details) {
        return new ShipError(ErrorType.Network, message, undefined, details);
    }
    /**
     * A deadline expired before the exchange completed.
     *
     * Statusless like its four client-only siblings: no exchange completed, so
     * there is no HTTP status to report. `isNetworkError()` is true — see
     * `ErrorType.Timeout` for why the category is shared and the type is not.
     */
    static timeout(message, details) {
        return new ShipError(ErrorType.Timeout, message, undefined, details);
    }
    static cancelled(message, details) {
        return new ShipError(ErrorType.Cancelled, message, undefined, details);
    }
    static file(message, details) {
        return new ShipError(ErrorType.File, message, undefined, details);
    }
    static config(message, details) {
        return new ShipError(ErrorType.Config, message, undefined, details);
    }
    static api(message, status = 500, details) {
        return new ShipError(ErrorType.Api, message, status, details);
    }
    /**
     * The platform is closed for maintenance (503).
     *
     * `message` is REQUIRED and has no default here. The API is the only
     * producer of that sentence, and a default in this file would be a second
     * owner of one fact — see CLAUDE.md, "The Constellation Law" (stopping
     * rule). It is also the first factory whose status is fixed rather than
     * defaulted: a maintenance refusal is 503 or it is not this error
     * (`build` is the second, on the same reasoning).
     */
    static maintenance(message, details) {
        return new ShipError(ErrorType.Maintenance, message, 503, details);
    }
    /**
     * The project could not be built as submitted (422).
     *
     * The second factory with a FIXED status, on Maintenance's reasoning: a
     * build verdict is 422 or it is not this error. `message` is the builder's
     * own sentence, relayed rather than rewritten, since the builder is the
     * throw site and the only party that knows why; `details` carries the log
     * (`BuildFailureDetails`), which is required for the same reason a verdict
     * without evidence is an opinion.
     */
    static build(message, details) {
        return new ShipError(ErrorType.Build, message, 422, details);
    }
    // Semantic-category guards. For specific-type checks, use
    // `error.type === ErrorType.X` directly or the generic `isType(t)`.
    /**
     * The caller is at fault — by HTTP's own definition of a 4xx, or by a type
     * that is client-attributable without ever having a status (`Config`,
     * `File`, raised locally by the SDK).
     *
     * Both arms are load-bearing, because type and status are independent
     * axes. `fromHttpResponse` trusts `body.error` only when it names a
     * server-producible type; a non-OK response without one is status-derived,
     * so a CDN 404 or any intermediary error arrives as `Api` — a server-fault
     * *type* carrying a client *status*. Judging by type alone would report it
     * as a platform failure and bury the server's own message.
     */
    isClientError() {
        if (ERROR_CATEGORIES.client.has(this.type))
            return true;
        return this.status !== undefined && this.status >= 400 && this.status < 500;
    }
    isNetworkError() {
        return ERROR_CATEGORIES.network.has(this.type);
    }
    isAuthError() {
        return ERROR_CATEGORIES.auth.has(this.type);
    }
    isType(errorType) {
        return this.type === errorType;
    }
}
/**
 * Type guard to check if an unknown value is a ShipError.
 *
 * Uses structural checking instead of instanceof to handle module duplication
 * in bundled applications where multiple copies of the ShipError class may exist.
 *
 * @example
 * if (isShipError(error)) {
 *   console.log(error.status, error.message);
 * }
 */
export function isShipError(error) {
    return (error !== null &&
        typeof error === 'object' &&
        'name' in error &&
        error.name === 'ShipError' &&
        'status' in error);
}
// =============================================================================
// EXTENSION MATCHING
// =============================================================================
/**
 * The rule for reading a file's extension: lowercase, after the last dot of
 * the last path segment. `null` when there is no extension to read.
 *
 * A leading dot names the file rather than its type, so `.gitignore` and
 * `.htaccess` have no extension — but `.env.exe` has `exe`.
 *
 * Segment-aware on purpose: the callers pass deploy PATHS, not basenames, and
 * a naive `lastIndexOf('.')` over `dir.v1/README` reads the extension
 * `v1/README` — safe only by accident, since no entry in a real blocklist
 * contains a slash, which is the kind of correctness nobody should have to
 * re-derive.
 *
 * **Private, and the reason is the asymmetry rather than any hazard.** Nothing
 * outside this file reads it: `isBlockedExtension` is the only question anyone
 * asks, and the API's refusal names the FILE, not its extension. Exporting a
 * pure function is harmless, which is exactly the argument that talks a
 * published package into surface it has not earned — and the costs do not
 * match, since adding an export later is free under the additive law while
 * removing one is a major. So it stays private until a caller exists. Its
 * behaviour is fenced through `isBlockedExtension`, which is where it is
 * observable.
 *
 * (`WEB_FILE_EXTENSIONS` above is private for a different reason — publishing
 * it would invite a wrong question. Both are private; only one is a hazard.)
 *
 * @example
 * fileExtension('virus.exe')          // 'exe'
 * fileExtension('assets/style.CSS')   // 'css'
 * fileExtension('dir.v1/README')      // null
 * fileExtension('.gitignore')         // null
 * fileExtension('file.')              // null
 */
function fileExtension(filename) {
    const basename = filename.replace(/\\/g, '/').split('/').pop() ?? '';
    const dotIndex = basename.lastIndexOf('.');
    if (dotIndex <= 0 || dotIndex === basename.length - 1)
        return null;
    return basename.slice(dotIndex + 1).toLowerCase();
}
/**
 * Whether a file is one the platform refuses to host.
 *
 * **The list is not this package's, and that separation is the point.** What
 * counts as a blocked extension is hosting POLICY — it evolves, it is enforced
 * at one security boundary, and `virus.exe` is a perfectly well-formed
 * filename that breaks nothing about the upload→serve round-trip. So the API
 * owns the list (`cloudflare/api/src/lib/blocklist.ts`) and delivers it as
 * `PlatformLimits.blockedExtensions`; a client passes what it was given.
 *
 * What lives here is the MATCHING RULE, and it earns its place by the
 * constellation law's own test. The list's drift is loud in both directions —
 * a stale client uploads a file the API refuses by name, on the first try.
 * A second *matcher* drifts SILENTLY in the one direction that matters: a
 * client stricter than the server refuses a legal file without the server ever
 * being asked, and no error names it. Two holders, silent drift, one owner.
 *
 * The `blocked` collection is required rather than defaulted: this predicate
 * guards a security boundary in the API, and a defaulted-empty argument there
 * would block nothing while reading as though it did. Callers holding a
 * possibly-absent wire field spell the fail-open themselves.
 *
 * @example
 * isBlockedExtension('virus.exe', ['exe'])   // true
 * isBlockedExtension('virus.EXE', ['exe'])   // true — case-insensitive
 * isBlockedExtension('style.css', ['exe'])   // false
 * isBlockedExtension('README', ['exe'])      // false — no extension
 */
export function isBlockedExtension(filename, blocked) {
    const ext = fileExtension(filename);
    if (ext === null)
        return false;
    return Array.isArray(blocked) ? blocked.includes(ext) : blocked.has(ext);
}
// =============================================================================
// PICKER ACCEPT HINT
// =============================================================================
/**
 * The extensions a browser file picker offers by default, grouped by role.
 *
 * Private on purpose: the only published form is `WEB_FILE_ACCEPT`, the
 * attribute value itself. A published set would invite a call site to ask it
 * whether a file is allowed — which is the one thing this list must never
 * answer. See `WEB_FILE_ACCEPT`.
 *
 * Extensionless files (`LICENSE`, most `.well-known` entries) are inexpressible
 * in `accept`, and reach a deployment by folder pick, ZIP, or drag-and-drop.
 */
const WEB_FILE_EXTENSIONS = [
    // Markup & documents
    'html',
    'htm',
    'xhtml',
    'xml',
    'txt',
    'md',
    'markdown',
    'pdf',
    'csv',
    // Data & config
    'json',
    'jsonc',
    'webmanifest',
    'map',
    'toml',
    'yaml',
    'yml',
    'rss',
    'atom',
    // Styles
    'css',
    'scss',
    'sass',
    'less',
    // Scripts & modules
    'js',
    'mjs',
    'cjs',
    'jsx',
    'ts',
    'tsx',
    'wasm',
    'vue',
    'svelte',
    // Images
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'avif',
    'svg',
    'ico',
    'bmp',
    'tif',
    'tiff',
    'heic',
    'heif',
    // Fonts
    'woff',
    'woff2',
    'ttf',
    'otf',
    'eot',
    // Audio
    'mp3',
    'wav',
    'ogg',
    'oga',
    'opus',
    'm4a',
    'aac',
    'flac',
    'weba',
    // Video
    'mp4',
    'webm',
    'ogv',
    'mov',
    'm4v',
    'avi',
    // 3D models
    'glb',
    'gltf',
    'usdz',
    // Text tracks
    'vtt',
    'srt',
    // Archive — a whole site in one file
    'zip',
];
/**
 * The `accept` attribute value for a browser file picker offering web files.
 *
 * **This is a hint, never a rule.** The API's blocklist is the platform's gate
 * and the only thing that decides what may be hosted; this constant decides
 * what a *file dialog* shows first. The two are not two halves of one policy,
 * and this one must never be consulted to accept or reject a file.
 *
 * The distinction is structural, not stylistic. `accept` can express only an
 * allowlist, while the platform's rule is a blocklist — so this list is
 * necessarily *narrower* than what the platform hosts, and reading it as
 * authority would reject files the platform serves happily. It is also not
 * enforcement in the browser's own terms: every file dialog offers an
 * all-files escape, and **drag-and-drop ignores `accept` entirely**. The
 * dropzone and the picker must reach the same verdict on the same files, and
 * they do — because the verdict is `validateFiles`, downstream of both.
 *
 * The invariant that matters — the picker must never offer a file the platform
 * will refuse — is fenced where the authority lives, in the API's own suite
 * (`cloudflare/api/tests/lib/blocklist.test.ts`), which reads this published
 * string and holds it against the list it owns. It sat here until the
 * blocklist became the API's, and moving it was the price of that: a fence
 * belongs with whichever side can change and break it.
 */
export const WEB_FILE_ACCEPT = WEB_FILE_EXTENSIONS.map((ext) => `.${ext}`).join(',');
// =============================================================================
// FILENAME CHARACTER VALIDATION
// =============================================================================
/**
 * Characters that are unsafe in filenames for static hosting.
 *
 * Blocks only characters that genuinely break the upload→serve round-trip:
 * - # ? %        URL round-trip breakers (fragment, query, encoding ambiguity)
 * - \            Path separator confusion (upload splits on backslash)
 * - < > "        XSS vectors with zero legitimate use in filenames
 * - \x00-\x1f \x7f  Control characters (header injection, display corruption)
 *
 * Everything else is allowed — browser percent-encodes, Worker decodes, R2 matches.
 */
// biome-ignore lint/suspicious/noControlCharactersInRegex: blocking control characters is this regex's purpose
export const UNSAFE_FILENAME_CHARS = /[\x00-\x1f\x7f#?%\\<>"]/;
/**
 * Check if a filename contains unsafe characters.
 *
 * @example
 * hasUnsafeChars('saved_resource(1).html')  // false — parentheses are safe
 * hasUnsafeChars('page[slug].js')           // false — brackets are safe
 * hasUnsafeChars('file#anchor.html')        // true  — # breaks URL resolution
 * hasUnsafeChars('file<tag>.html')          // true  — < is an XSS vector
 */
export function hasUnsafeChars(filename) {
    return UNSAFE_FILENAME_CHARS.test(filename);
}
// =============================================================================
// UNBUILT PROJECT MARKERS
// =============================================================================
/**
 * Path segment names that indicate an unbuilt project was uploaded instead of build output.
 * Used for early detection in CLI, browser, and server validation.
 */
export const UNBUILT_PROJECT_MARKERS = new Set([
    'node_modules',
    'package.json',
]);
/**
 * Check if a file path contains an unbuilt project marker.
 *
 * @example
 * hasUnbuiltMarker('node_modules/react/index.js')  // true
 * hasUnbuiltMarker('package.json')                  // true
 * hasUnbuiltMarker('dist/index.html')               // false
 */
export function hasUnbuiltMarker(filePath) {
    const segments = filePath.replace(/\\/g, '/').split('/').filter(Boolean);
    return segments.some((s) => UNBUILT_PROJECT_MARKERS.has(s));
}
// =============================================================================
// DEPLOY RULES
// =============================================================================
/**
 * The page a deployment serves at `/`. Every deployment has one at its root:
 * ShipStatic publishes websites, and a website starts at its home page.
 * Beside {@link DEPLOYMENT_CONFIG_FILENAME}, the other file that sets a
 * deployment up.
 */
export const DEPLOYMENT_INDEX_FILENAME = 'index.html';
/** Max deploy-path bytes: R2 keys cap at 1024 bytes and the deployment prefix consumes up to 22. */
export const MAX_PATH_BYTES = 1000;
/**
 * A deploy path as the deployment stores it: ONE leading slash names the same
 * path, so it is dropped. Every rule and every store compares paths in this
 * form, so `index.html` and `/index.html` are one file wherever they are
 * judged. Two leading slashes are not one, so `//index.html` is left as it is
 * and {@link isDeployablePath} refuses its empty segment. That makes the
 * function idempotent: a path canonical once is canonical however many times
 * a boundary asks again, so every judge and every store sees one identity.
 */
export function canonicalDeployPath(path) {
    return path.startsWith('/') && !path.startsWith('//') ? path.slice(1) : path;
}
/**
 * What a deployment is told when it names one path twice: two files where
 * the deployment holds one. The deploy rules say it, and so does the API's
 * intake, which refuses the repeat before it can index the files by path.
 */
export function namedTwice(path) {
    return `The file ${path} appears twice.`;
}
/**
 * Whether a deploy path can be served as it is: within {@link MAX_PATH_BYTES},
 * with no empty segment, none over 255 characters, and none starting with a
 * dot, root-level `.well-known` excepted (RFC 8615). The router refuses to
 * serve a hidden segment, so a file at one could never be reached. Read in
 * its canonical form ({@link canonicalDeployPath}).
 */
export function isDeployablePath(path) {
    const normalized = canonicalDeployPath(path);
    if (new TextEncoder().encode(normalized).length > MAX_PATH_BYTES)
        return false;
    return normalized
        .split('/')
        .every((segment, index) => segment !== '' &&
        segment.length <= 255 &&
        (!segment.startsWith('.') || (segment === '.well-known' && index === 0)));
}
/**
 * Whether a file name can make the upload-to-serve round trip, and if not, the
 * sentence that says why, naming the file. Only what genuinely breaks it is
 * refused: characters that break a URL or open an XSS vector
 * ({@link hasUnsafeChars}), a leading or trailing space, a trailing dot, and
 * a reserved system name. A `..` segment is a segment starting with a dot,
 * which {@link isDeployablePath} refuses, so `report..final.pdf` is a name.
 */
export function validateFileName(filename) {
    const refuse = (why) => ({ valid: false, reason: `File name "${filename}" ${why}.` });
    if (filename.trim() === '')
        return { valid: false, reason: 'A file has no name.' };
    if (hasUnsafeChars(filename))
        return refuse('contains unsafe characters');
    if (filename.startsWith(' ') || filename.endsWith(' ')) {
        return refuse('cannot start or end with spaces');
    }
    if (filename.endsWith('.'))
        return refuse('cannot end with dots');
    const name = filename.split('/').pop() || filename;
    if (/^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])(\.|$)/i.test(name)) {
        return refuse('uses a reserved system name');
    }
    return { valid: true };
}
/**
 * Whether a file sent to a deployment is published. An empty one is not: no
 * deployment stores an empty file, so it is left out rather than refused, by
 * every judge and every store alike. The rules about the deployment as a whole
 * still see it, which is how an empty `index.html` is told apart from a
 * missing one.
 */
export function isPublishedFile(file) {
    return file.size > 0;
}
/**
 * A limit's sentence, followed by the way forward the API delivered for that
 * limit ({@link PlatformLimits.suggestions}), verbatim. The API writes it with
 * the function its own plans answer to, so a refusal ends the same way
 * wherever it is made, and no client learns what a plan is.
 */
function withSuggestion(sentence, limits, key) {
    const suggestion = limits.suggestions?.[key];
    return suggestion ? `${sentence} ${suggestion}` : sentence;
}
/**
 * Every rule each file of a deployment is held to, in the order they apply:
 * a file that breaks two reports the first, because fixing it may fix the
 * other. Every sentence names its file once and is complete on its own, so
 * every surface shows it as it is.
 *
 * The API judges a deployment by these rules at its declaration, and every
 * client judges its files by them before sending a byte, so a refusal reads
 * the same wherever it is made. Read through {@link deployRefusal}, or
 * {@link firstBrokenRule} and {@link brokenSetRule} where a caller judges a
 * deployment as it walks it.
 */
export const FILE_RULES = [
    {
        name: 'name',
        broken: ({ path }) => !validateFileName(path).valid,
        sentence: ({ path }) => validateFileName(path).reason ?? `File name "${path}" is not allowed.`,
    },
    {
        // The list is the platform's, delivered through `/limits`. Absent means
        // no check here, never an empty policy: the API holds the list.
        name: 'extension',
        broken: ({ path }, limits) => isBlockedExtension(path, limits.blockedExtensions ?? []),
        sentence: ({ path }) => `File "${path}" has an extension that is not allowed.`,
    },
    {
        name: 'project',
        broken: ({ path }) => hasUnbuiltMarker(path),
        sentence: ({ path }) => `File "${path}" belongs to a project that isn't built yet. Build it, then publish the folder the build makes, like dist.`,
    },
    {
        name: 'path',
        broken: ({ path }) => !isDeployablePath(path),
        sentence: ({ path }) => `File "${path}" can't be published: a folder or file name in its path starts with a dot, is empty, or is too long.`,
    },
    {
        name: 'fileSize',
        broken: ({ size }, limits) => size > limits.maxFileSize,
        sentence: ({ path }, limits) => withSuggestion(`File "${path}" is too large. Maximum ${formatFileSize(limits.maxFileSize)} allowed.`, limits, 'maxFileSize'),
    },
    {
        name: 'totalSize',
        broken: ({ totalSize }, limits) => totalSize > limits.maxTotalSize,
        sentence: ({ totalSize }, limits) => withSuggestion(`Files add up to ${formatFileSize(totalSize)}. Maximum ${formatFileSize(limits.maxTotalSize)} allowed.`, limits, 'maxTotalSize'),
    },
];
/** Whether a path names an HTML page, the kind of file a site's main page is. */
function isPagePath(path) {
    return /\.html?$/i.test(path);
}
/**
 * What to do about a missing home page, read from what the files show, so
 * the refusal names the step that fits: the folder that holds the site when
 * it sits one folder down (the site published from the folder above its
 * build), the page to rename when there are pages but none is the home page
 * (a single `report.html`), and the small page to add when there is no page
 * at all (an image or a PDF published on its own, measured as the most
 * common case).
 */
function missingIndexAdvice(paths) {
    const folder = nestedIndexFolder(paths);
    if (folder)
        return `There's one in ${folder}/. If that's your site, publish that folder instead.`;
    if (paths.some(isPagePath)) {
        return `If one of your pages is the main one, put it at the top and name it ${DEPLOYMENT_INDEX_FILENAME}.`;
    }
    return `To publish a file on its own, like an image or a PDF, add a small ${DEPLOYMENT_INDEX_FILENAME} that links to it.`;
}
/**
 * Where the home page is, when it is one folder down: the shallowest nested
 * `index.html`, if exactly one folder holds it. A site deployed from the
 * folder above its build is the common way to miss the root, so the refusal
 * names the folder to publish instead.
 */
function nestedIndexFolder(paths) {
    const suffix = `/${DEPLOYMENT_INDEX_FILENAME}`;
    const depth = (path) => path.split('/').length;
    const nested = paths.filter((path) => path.endsWith(suffix));
    if (nested.length === 0)
        return undefined;
    const shallowest = Math.min(...nested.map(depth));
    const top = nested.filter((path) => depth(path) === shallowest);
    return top.length === 1 ? top[0]?.slice(0, -suffix.length) : undefined;
}
/** The paths a deployment publishes, out of the files it was sent, in their canonical form. */
function publishedPaths(files) {
    return files.filter(isPublishedFile).map((file) => canonicalDeployPath(file.path));
}
/** The first path the files name twice, empty files included, or `undefined`. */
function repeatedPath(files) {
    const seen = new Set();
    for (const { path } of files) {
        const canonical = canonicalDeployPath(path);
        if (seen.has(canonical))
            return canonical;
        seen.add(canonical);
    }
    return undefined;
}
/**
 * The rules asked of the deployment as a whole, after every file has passed
 * {@link FILE_RULES}: it has files, it names each path once, it has not more
 * than the plan allows, and it has a home page at its root. They read the
 * files as SENT, an empty one included, and count only what is published
 * ({@link isPublishedFile}), so the home-page rule can say an `index.html` is
 * empty rather than missing. Paths compare in their canonical form
 * ({@link canonicalDeployPath}). These name no one file, so a surface that
 * lists refusals per file files them under the deployment.
 */
export const SET_RULES = [
    {
        name: 'files',
        broken: (files) => publishedPaths(files).length === 0,
        sentence: () => 'There are no files to publish.',
    },
    {
        name: 'unique',
        broken: (files) => repeatedPath(files) !== undefined,
        sentence: (files) => namedTwice(repeatedPath(files) ?? ''),
    },
    {
        name: 'filesCount',
        broken: (files, limits) => publishedPaths(files).length > limits.maxFilesCount,
        sentence: (files, limits) => withSuggestion(`Too many files (${publishedPaths(files).length}). Maximum ${limits.maxFilesCount} files allowed.`, limits, 'maxFilesCount'),
    },
    {
        name: 'index',
        broken: (files) => !publishedPaths(files).includes(DEPLOYMENT_INDEX_FILENAME),
        sentence: (files) => {
            if (files.some((file) => canonicalDeployPath(file.path) === DEPLOYMENT_INDEX_FILENAME)) {
                return `The ${DEPLOYMENT_INDEX_FILENAME} at the top of your site is empty. It's the page visitors see first, so it needs content.`;
            }
            return `There's no ${DEPLOYMENT_INDEX_FILENAME} at the top of your site. It's the page visitors see first, so your site needs one. ${missingIndexAdvice(publishedPaths(files))}`;
        },
    },
];
/** The first rule a file breaks, or `undefined`. */
export function firstBrokenRule(file, limits) {
    return FILE_RULES.find((rule) => rule.broken(file, limits));
}
/** The first rule the deployment as a whole breaks, or `undefined`. */
export function brokenSetRule(files, limits) {
    return SET_RULES.find((rule) => rule.broken(files, limits));
}
/**
 * Why a deployment of these files is refused, as the one sentence a person
 * reads, or `undefined` when every rule holds: each published file in order
 * against {@link FILE_RULES}, then the whole against {@link SET_RULES}. Pass
 * the files as sent, empty ones included: an empty file is left out of the
 * per-file rules and the totals, never refused.
 */
export function deployRefusal(files, limits) {
    let totalSize = 0;
    for (const { path, size } of files.filter(isPublishedFile)) {
        totalSize += size;
        const file = { path, size, totalSize };
        const rule = firstBrokenRule(file, limits);
        if (rule)
            return rule.sentence(file, limits);
    }
    return brokenSetRule(files, limits)?.sentence(files, limits);
}
// =============================================================================
// CREDENTIAL SHAPES
// =============================================================================
// The one address for credential vocabulary: where human identity lives
// (AUTH_BASE_PATH), how a request is authorized (AuthMethod), the shapes
// that distinguish populations on the wire (API_KEY, DEPLOY_TOKEN,
// OAUTH_TOKEN, CALLER), the two halves of the one Bearer slot
// (readBearerValue reads it, classifyToken/TokenKind dispatch on what came
// out), and the delegated-access scopes (OAuthScope).
//
// THE SHAPE LAW, in three clauses, over the `Authorization: Bearer` slot's
// three populations below. The deployment claim code is the API's own
// (`AUTH.CLAIM`, server-side: the API mints it and the API validates it, so
// it has one holder and stays there) and shares only clause 1 — it is the
// platform's one deliberately BARE secret, because it never enters the
// Bearer slot: minted into one URL, consumed by one endpoint's one field,
// its context names it and a prefix would restate its route.
// `tests/validation-constants.test.ts` holds the clauses over this file's
// populations; the API's suite holds its own.
//
//  1. ONE ENTROPY STANDARD. Every minted secret is `HEX_LENGTH` hex characters
//     — one width for the whole platform, so "how long is a credential" has a
//     single answer rather than one per population. Generators read the width
//     from the population's own constant, so a minted value and an accepted
//     value cannot differ.
//
//  2. EVERY BEARER POPULATION IS NAMED BY ITS PREFIX. A credential says what
//     it is before anything parses it — which is what lets `classifyToken`
//     below dispatch three populations sharing one `Authorization: Bearer`
//     slot, and what lets a value found in a log, a support ticket or a
//     pasted URL be recognised and revoked on sight. The OAuth access token
//     was this clause's one standing exception until 2026-08-14 — the
//     authorization server it was born on had no mint hook to give it a
//     prefix, and its successor does.
//
//  3. NO PREFIX IS A PREFIX OF ANOTHER. This is what makes the dispatch
//     order-independent, and it is the reason the populations are named on
//     different axes (`ship-` for the product, `deploy-` for the capability)
//     rather than sharing a stem. A `ship-` / `ship-deploy-` pair
//     reads tidier and is a trap: every deploy token would also match the
//     API-key branch, leaving correctness resting on the order of two `if`s.
/**
 * Where human identity is mounted on the API host. The API mounts Better
 * Auth at this path (sign-in, sign-out, session reads, admin impersonation)
 * and the web console's auth client posts to it — shared here so the two
 * halves of the auth pair agree by construction, the same way both sides
 * already share the credential prefixes below.
 */
export const AUTH_BASE_PATH = '/auth';
/**
 * How a request (or recorded activity) was authorized.
 *
 * Client populations: `SESSION` (first-party cookie), `API_KEY` (`ship-`
 * key), `TOKEN` (`deploy-` deploy token), `AGENT` (anonymous, no credential:
 * public deploys are granted the public-account identity per request),
 * `OAUTH` (delegated access token). The one server population:
 * `SYSTEM` (scheduled/background jobs). Webhook receipt is deliberately not
 * a population: a signed delivery is verified, never authorized — it acts
 * as no one and audits as no one.
 */
export const AuthMethod = {
    SESSION: 'session',
    API_KEY: 'apiKey',
    TOKEN: 'token',
    AGENT: 'agent',
    OAUTH: 'oauth',
    SYSTEM: 'system',
};
/**
 * Shape constants for API keys (`ship-{32 hex chars}`).
 * Single source of truth used by validation utilities and auth middleware.
 */
export const API_KEY = {
    /** Prefix that identifies an API key. */
    PREFIX: 'ship-',
    /** Number of hex characters following the prefix. */
    HEX_LENGTH: 32,
    /** Total length of an API key including prefix (`PREFIX.length + HEX_LENGTH = 37`). */
    TOTAL_LENGTH: 37,
    /** Number of trailing characters used to display a redacted hint (e.g. last 4). */
    HINT_LENGTH: 4,
};
/**
 * Shape constants for deploy tokens (`deploy-{32 hex chars}`).
 * Single source of truth used by validation utilities and auth middleware.
 *
 * Deliberately the same width as `API_KEY`: both are minted by one generator
 * and classified by prefix alone, so a length that differed between them
 * would be a second thing to know about a credential whose prefix already
 * says what it is.
 */
export const DEPLOY_TOKEN = {
    /** Prefix that identifies a deploy token. */
    PREFIX: 'deploy-',
    /** Number of hex characters following the prefix. */
    HEX_LENGTH: 32,
    /** Total length of a deploy token including prefix (`PREFIX.length + HEX_LENGTH = 39`). */
    TOTAL_LENGTH: 39,
};
/**
 * Shape constants for OAuth access tokens (`oauth-{32 hex chars}`) — the
 * delegated population, minted by the platform's own authorization server
 * for a connected app acting on a user's behalf.
 *
 * Same width as the other two, and for the same reason: one entropy standard
 * across the platform, so "how long is a credential" has one answer.
 *
 * **This population is the access token alone.** Refresh tokens, authorization
 * codes and client secrets are deliberately NOT here and are deliberately not
 * prefixed by this constant: none of them ever enters the `Authorization:
 * Bearer` slot — a refresh token is posted as a form field to the token
 * endpoint, which knows what it is receiving — so `classifyToken` never sees
 * one and a prefix would name a population no dispatcher dispatches. The same
 * reasoning that keeps the deployment claim code bare.
 *
 * **The prefix must be applied at the MINT, never as a display wrapper.** The
 * authorization server hashes what it stores and the API hashes what it is
 * presented, so the prefix has to be inside the hashed string on both sides.
 * `@better-auth/oauth-provider` offers a `prefix.opaqueAccessToken` option
 * that prepends AFTER hashing and strips on its own read paths; using it would
 * store a hash of the UNPREFIXED token and silently break the platform's read
 * arm. The API therefore mints through `generateOpaqueAccessToken` — recorded
 * beside the config in `cloudflare/api/src/lib/auth/instance.ts`.
 */
export const OAUTH_TOKEN = {
    /** Prefix that identifies an OAuth access token. */
    PREFIX: 'oauth-',
    /** Number of hex characters following the prefix. */
    HEX_LENGTH: 32,
    /** Total length including prefix (`PREFIX.length + HEX_LENGTH = 38`). */
    TOTAL_LENGTH: 38,
};
/**
 * Shape constants for caller identifiers (the `X-Caller` instance-identity
 * header — rate-limit bucketing for multi-tenant orchestrators). The API
 * normalizes case and silently ignores malformed values (the header is
 * unauthenticated); clients validate at the boundary via `validateCaller`,
 * so a value the server would drop fails fast instead.
 */
export const CALLER = {
    /** HTTP header name. */
    HEADER: 'X-Caller',
    /** Maximum identifier length. */
    MAX_LENGTH: 128,
    /** Allowed characters: alphanumeric, dot, underscore, hyphen. */
    PATTERN: /^[a-zA-Z0-9._-]+$/,
};
/**
 * The account a session request is about.
 *
 * A person may belong to several accounts, so a request made with a browser
 * session names the one it means, on every request to an account route; the
 * console takes the value from its URL (`/<account>/`). The header names and
 * membership authorizes: the API resolves the person's membership in the
 * account named, answers 404 `details.account: 'unknown'` for an account the
 * person is not in (or one that does not exist), and 400 when a session
 * request names none. Machine credentials never send it, since a key, a
 * deploy token or an OAuth grant already belongs to one account. One owner
 * for the name, since the API's CORS allowlist and every browser transport
 * spell it.
 */
export const ACCOUNT_HEADER = 'X-Account';
/**
 * Token populations distinguishable by shape. The platform carries every
 * client token in one wire slot (`Authorization: Bearer <value>`) and
 * classifies by value, never by a side channel — this is the classifier.
 *
 * `API_KEY`, `DEPLOY_TOKEN` and `OAUTH` *are* `AuthMethod.API_KEY`,
 * `AuthMethod.TOKEN` and `AuthMethod.OAUTH` — the equality is structural, so
 * a classification flows straight into an auth method and the trio can never
 * drift.
 *
 * `OPAQUE` is any other value, and since 2026-08-14 it names NO population:
 * every credential this platform mints for the Bearer slot carries a prefix,
 * so an opaque bearer is a bearer we did not mint. It stays a member rather
 * than becoming a `null` return because a dispatcher with a total codomain
 * reads better than one with an absence in it — and because it is where a
 * future population would land before anyone gave it a shape, which is
 * exactly what the OAuth token itself did until its prefix existed.
 */
export const TokenKind = {
    API_KEY: AuthMethod.API_KEY,
    DEPLOY_TOKEN: AuthMethod.TOKEN,
    OAUTH: AuthMethod.OAUTH,
    OPAQUE: 'opaque',
};
/**
 * Classify a client token by shape. The single dispatch used by both sides
 * of the wire: API auth middleware (which population is this credential?)
 * and SDK validation (which format rules apply before sending?). Sharing it
 * is what guarantees client and server can never disagree on dispatch.
 */
export function classifyToken(token) {
    if (token.startsWith(API_KEY.PREFIX))
        return TokenKind.API_KEY;
    if (token.startsWith(DEPLOY_TOKEN.PREFIX))
        return TokenKind.DEPLOY_TOKEN;
    if (token.startsWith(OAUTH_TOKEN.PREFIX))
        return TokenKind.OAUTH;
    return TokenKind.OPAQUE;
}
/** The auth-scheme, lowercased — the form the comparison is made in. */
const BEARER_SCHEME = 'bearer ';
/**
 * Read the credential out of an `Authorization` header value — the step
 * BEFORE `classifyToken`, and the other half of the one wire slot this
 * section owns.
 *
 * Returns the credential's own bytes, or `null` when the header carries a
 * foreign scheme or nothing after the scheme.
 *
 * **The scheme is folded; the credential is not.** RFC 7235 §2.1 makes the
 * auth-scheme case-insensitive, so `bearer`, `Bearer` and `BEARER` are the
 * same header. The value after it is opaque and is compared literally
 * everywhere it is used — `ship-`/`deploy-`/`oauth-` are lowercase hex, and
 * folding them would make a credential match values it is not.
 *
 * **This platform has paid for the rule twice, which is why it has an owner
 * rather than a convention.** A spec-conformant `bearer ship-…` client was
 * refused for as long as the API's scheme test was spelled case-sensitively;
 * and `@better-auth/oauth-provider` carries the same defect in four places
 * today (`startsWith("Bearer ")`), which is precisely why the platform folds
 * the scheme itself and hands the provider a bare token.
 *
 * **ABSENCE is deliberately not this function's business.** A missing header
 * and an unreadable one are different facts, and the callers that care split
 * on them: the API worker's middleware distinguishes `absent` (the only
 * anonymous path) from `unreadable` (a presented credential that is refused),
 * and collapsing the two here would take that distinction away from the layer
 * that needs it. Callers check for the header themselves and pass its value.
 *
 * **Why this lives in the constitution rather than in a worker's `shared/`.**
 * It is the same wire boundary `classifyToken` already owns — one reads the
 * slot, the other dispatches on what came out — and a rule with two holders
 * whose drift is silent earns exactly one owner regardless of what the
 * convoy costs. The estate's recorded refusal to own a `Bearer` CONSTANT
 * stands and is a different thing: that is RFC vocabulary, the same reason
 * this package owns no `"POST"`. A parser is not a spelling.
 */
export function readBearerValue(header) {
    if (header.slice(0, BEARER_SCHEME.length).toLowerCase() !== BEARER_SCHEME)
        return null;
    return header.slice(BEARER_SCHEME.length) || null;
}
/**
 * OAuth scope vocabulary for delegated third-party access tokens.
 * Single source of truth used by the authorization server (advertised in
 * `scopes_supported`), the API's scope-enforcement middleware, and consent UI
 * copy. The standard `offline_access` scope (refresh tokens) is not platform
 * vocabulary and is deliberately absent — the middleware never checks it.
 *
 * Deliberately absent by design: any `tokens:*` scope, `account:write`, or
 * admin scope — a delegated app must never mint credentials, delete the
 * account, or act as admin.
 */
export const OAuthScope = {
    ACCOUNT_READ: 'account:read',
    DEPLOYMENTS_READ: 'deployments:read',
    DEPLOYMENTS_WRITE: 'deployments:write',
    DOMAINS_READ: 'domains:read',
    DOMAINS_WRITE: 'domains:write',
};
// =============================================================================
// DEPLOYMENT CONFIGURATION CONSTANTS
// =============================================================================
export const DEPLOYMENT_CONFIG_FILENAME = 'ship.json';
/**
 * Assert that a ship.json file is *syntactically* loadable. Syntax only —
 * never schema.
 *
 * ship.json is validated and compiled on the server, deliberately: the schema
 * and the compiler evolve, and a client that judged them would reject configs
 * a newer platform accepts. That reasoning bounds what a client may check to
 * the properties which are true of *every* past and future schema:
 *
 *   1. it parses as JSON — JSON syntax is frozen (RFC 8259), so text that
 *      does not parse can never be a valid config;
 *   2. its top level is an object — ship.json is `{ ... }` in every version.
 *
 * Both are monotonic: neither can ever reject something the server would
 * accept. Everything beyond them (field names, types, rule semantics, which
 * keys are permitted) stays server-side, where it can change.
 *
 * The payoff is the common case. Hand-edited JSON fails on a trailing comma,
 * a `//` comment, single quotes, unquoted keys, or smart quotes pasted from
 * documentation — mistakes that otherwise cost a full upload round-trip to
 * discover. A UTF-8 BOM (Windows editors, PowerShell redirects) is stripped
 * before parsing rather than rejected, because the server accepts it too;
 * diverging there would reintroduce exactly the false rejection this
 * function exists to avoid.
 *
 * @throws {ShipError} `ErrorType.Config` — the same type the server's own
 * config rejection carries, so the error contract is identical wherever the
 * failure is detected.
 */
export function assertShipJsonSyntax(text) {
    const withoutBom = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    let parsed;
    try {
        parsed = JSON.parse(withoutBom);
    }
    catch (error) {
        throw ShipError.config(`invalid JSON format in config: ${error.message}`, {
            filePath: DEPLOYMENT_CONFIG_FILENAME,
        });
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw ShipError.config(`${DEPLOYMENT_CONFIG_FILENAME} must contain a JSON object`, {
            filePath: DEPLOYMENT_CONFIG_FILENAME,
        });
    }
}
// =============================================================================
// VALIDATION UTILITIES
// =============================================================================
/**
 * Shared rule for prefixed credentials: `{PREFIX}{HEX_LENGTH hex chars}`.
 * The regex derives from the shape constants, so the validators can never
 * drift from the shapes `classifyToken` dispatches on.
 */
function validatePrefixedCredential(value, shape, label) {
    if (!value.startsWith(shape.PREFIX)) {
        throw ShipError.validation(`${label} must start with "${shape.PREFIX}"`);
    }
    if (value.length !== shape.TOTAL_LENGTH) {
        throw ShipError.validation(`${label} must be ${shape.TOTAL_LENGTH} characters total (${shape.PREFIX} + ${shape.HEX_LENGTH} hex chars)`);
    }
    const hexPart = value.slice(shape.PREFIX.length);
    if (!new RegExp(`^[a-f0-9]{${shape.HEX_LENGTH}}$`, 'i').test(hexPart)) {
        throw ShipError.validation(`${label} must contain ${shape.HEX_LENGTH} hexadecimal characters after "${shape.PREFIX}" prefix`);
    }
}
/**
 * Validate API key format
 */
export function validateApiKey(apiKey) {
    validatePrefixedCredential(apiKey, API_KEY, 'API key');
}
/**
 * Validate deploy token format
 */
export function validateDeployToken(deployToken) {
    validatePrefixedCredential(deployToken, DEPLOY_TOKEN, 'Deploy token');
}
/**
 * Validate OAuth access token format
 */
export function validateOAuthToken(oauthToken) {
    validatePrefixedCredential(oauthToken, OAUTH_TOKEN, 'OAuth access token');
}
/**
 * Validate a client token of any population. Classifies by shape and applies
 * the matching format rules: all three prefixed populations are validated
 * strictly; an OPAQUE token only needs to be non-empty.
 *
 * **The OPAQUE arm stays permissive on purpose**, even though the platform no
 * longer mints an unprefixed credential. It is the fallback for a population
 * that does not exist yet, and a client refusing a shape the server would
 * accept is the one failure mode this boundary must never have — the server
 * decides, and it refuses an unrecognised bearer anyway. Unprefixed OAuth
 * tokens from before 2026-08-14 land here and are refused server-side, which
 * is correct: they were revoked by the change, not grandfathered.
 */
export function validateToken(token) {
    switch (classifyToken(token)) {
        case TokenKind.API_KEY:
            validateApiKey(token);
            return;
        case TokenKind.DEPLOY_TOKEN:
            validateDeployToken(token);
            return;
        case TokenKind.OAUTH:
            validateOAuthToken(token);
            return;
        case TokenKind.OPAQUE:
            if (!token)
                throw ShipError.validation('Token must be a non-empty string');
    }
}
/**
 * Validate a caller identifier against the `CALLER` shape. The server
 * silently ignores malformed values (the header is unauthenticated); clients
 * call this at configuration time so the drop never silently happens.
 */
export function validateCaller(caller) {
    if (!caller || caller.length > CALLER.MAX_LENGTH || !CALLER.PATTERN.test(caller)) {
        throw ShipError.validation(`Caller must be 1-${CALLER.MAX_LENGTH} characters: letters, digits, dots, underscores, or hyphens`);
    }
}
/**
 * Validate API URL format
 */
export function validateApiUrl(apiUrl) {
    try {
        const url = new URL(apiUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
            throw ShipError.validation('API URL must use http:// or https:// protocol');
        }
        if (url.pathname !== '/' && url.pathname !== '') {
            throw ShipError.validation('API URL must not contain a path');
        }
        if (url.search || url.hash) {
            throw ShipError.validation('API URL must not contain query parameters or fragments');
        }
    }
    catch (error) {
        if (isShipError(error)) {
            throw error;
        }
        throw ShipError.validation('API URL must be a valid URL');
    }
}
/**
 * Check if a string matches the deployment identifier pattern (word-word-alphanumeric7).
 * Example: "happy-cat-abc1234.shipstatic.com"
 */
export function isDeployment(input) {
    return /^[a-z]+-[a-z]+-[a-z0-9]{7}(\.[a-z0-9.-]+)?$/i.test(input);
}
/**
 * The envelope a requested lifetime must fit — one word, one grammar, wherever
 * the platform lets a caller choose how long something lives.
 *
 * Two resources wear it: `TokenCreateOptions.ttl` and
 * `DeploymentUploadOptions.ttl`. It lives here rather than on the server by
 * the format-vs-policy rule — a client can decide offline whether a duration
 * is well-formed, and the API rejects the same value the same way. What is
 * NOT here is any per-plan ceiling: no such policy exists, and one delivered
 * speculatively through `/limits` would be an owner for a decision nobody has
 * made.
 */
export const TTL_CONSTRAINTS = {
    /**
     * Shortest requestable lifetime, in seconds. One rather than zero: a
     * deployment that expires the instant it is created is not a shorter lease,
     * it is a deploy that was never live, and `0` is how an unset variable
     * arrives.
     */
    MIN_SECONDS: 1,
    /** Longest requestable lifetime, in seconds — one year. */
    MAX_SECONDS: 365 * 24 * 60 * 60,
};
/**
 * Validate a requested lifetime in SECONDS and return it, or `undefined` when
 * none was asked for.
 *
 * **A duration, never an instant.** The caller says how long; the server owns
 * what time it is and stamps the expiry — so a client's clock, however wrong,
 * cannot shorten or extend a lease. That is the tokens precedent, and it is
 * why this rule measures a count of seconds rather than checking a timestamp
 * against `now`.
 *
 * Fractions are refused rather than rounded: a caller who wrote `1.5` meant
 * something the wire cannot carry, and silently choosing `1` or `2` for them
 * is a decision the platform has no standing to make.
 *
 * Single source of truth shared by the API (the tokens route and the deploy
 * schema), the SDK's request boundary, and the CLI's parser.
 */
export function validateTtl(value) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        throw ShipError.validation('TTL must be a number of seconds');
    }
    if (!Number.isInteger(value)) {
        throw ShipError.validation('TTL must be a whole number of seconds');
    }
    if (value < TTL_CONSTRAINTS.MIN_SECONDS || value > TTL_CONSTRAINTS.MAX_SECONDS) {
        throw ShipError.validation(`TTL must be between ${TTL_CONSTRAINTS.MIN_SECONDS} and ${TTL_CONSTRAINTS.MAX_SECONDS} seconds`);
    }
    return value;
}
// =============================================================================
// CONSOLE PATHS
// =============================================================================
// The console's path grammar: every console path a server producer emits,
// and the readers that tell those paths apart.
//
// One fact, two holders. The console mounts these paths as routes, and the
// API, its letters and its billing returns emit them as links; a drift
// between the two is a link that 404s with nothing to report it, and no
// fence can reach across the two repositories. So the grammar lives here
// and both import it.
//
// The console's URL names the account: every page inside an account lives
// under `/<account>/`, where `<account>` is the account's id
// (`Account.account`). Every section is also reachable at the root, where
// the console opens it in the account the person entered last (or, for a
// section flagged `owner`, in their own); that bare form is what a producer
// whose subject is a person emits. Outside both sit the doors, which exist
// before an account is known, and the operator partition.
/**
 * The sections of an account, by first segment, each mounted twice by the
 * console: under `/<account>/` and at the root.
 *
 * `owner` marks a section that means the person's OWN account: a bare path
 * into it opens the person's own account rather than the one entered last.
 * A pricing link's `/upgrade/pro` and the published API-key link speak about
 * the reader's plan and key, and `/success` is where a purchase lands: only
 * an owner buys, and a person owns exactly one account. Modals inside a
 * section carry their own owner rule in the console; this flag is the
 * section's alone.
 */
export const SECTIONS = {
    new: { owner: false },
    deployments: { owner: false },
    domains: { owner: false },
    settings: { owner: false },
    // A claim link (`/claim/<code>`) is mailed and answered without an account,
    // so it opens where `/` opens: the account the person is standing in. The
    // site is kept there. Not an owner section, since a member may keep a
    // site in a team.
    claim: { owner: false },
    upgrade: { owner: true },
    success: { owner: true },
    'api-key': { owner: true },
};
/** The pages that exist before an account is known, by first segment. */
const DOOR_SEGMENTS = ['login', 'register', 'logout', 'invitation', 'consent'];
/** The operator partition's first segment. */
const ADMIN_SEGMENT = 'admin';
// Sections, doors and the operator partition are disjoint sets of first
// segments: a path's first segment decides which of the three it is, and
// anything that is none of them names an account. A shared name fails the
// build here.
const disjoint = true;
void disjoint;
/**
 * The query markers a Stripe return lands with, each written by a builder
 * below and read by `billingReturnOf`, so neither side spells one.
 */
const BILLING_RETURNS = {
    checkout: ['upgrading', 'true'],
    portal: ['billing', 'returned'],
};
const marker = (kind) => BILLING_RETURNS[kind].join('=');
/** A link with an optional `?next=`, the console's own return path. */
const withNext = (door, next) => next ? `${door}?next=${encodeURIComponent(next)}` : door;
/**
 * The paths inside one account, or, with no account, the bare form the
 * console opens in the account entered last.
 *
 * ```ts
 * consolePaths(account).domains.connect('www.example.com')
 * // '/<account>/domains/www.example.com/connect'
 * consolePaths().apiKey()
 * // '/api-key'
 * ```
 */
export function consolePaths(account) {
    const base = account ? `/${account}` : '';
    const at = (path) => `${base}${path}`;
    return {
        dashboard: () => base || '/',
        newSite: () => at('/new'),
        deployments: Object.assign(() => at('/deployments'), {
            upload: () => at('/deployments/upload'),
        }),
        domains: Object.assign(() => at('/domains'), {
            connect: (domain) => at(`/domains/${domain}/connect`),
            deployment: (domain) => at(`/domains/${domain}/deployment`),
        }),
        settings: (fragment) => at(fragment ? `/settings#${fragment}` : '/settings'),
        /** The claim of an anonymous deployment into this account; the API mails the bare form. */
        claim: (code) => at(`/claim/${code}`),
        /** Settings, marked as the landing of a Stripe Customer Portal return. */
        settingsAfterBilling: () => at(`/settings?${marker('portal')}`),
        upgrade: (plan) => at(plan ? `/upgrade/${plan}` : '/upgrade'),
        success: () => at('/success'),
        /** The success page, marked as the landing of a Stripe Checkout return. */
        successAfterCheckout: () => at(`/success?${marker('checkout')}`),
        apiKey: () => at('/api-key'),
    };
}
/**
 * The login door: `/login`, the journey on its query. `login.link` is the page
 * a sign-in email's link opens, `/login/link/<token>`: the door's object, the
 * link's token, rides the path, and the journey still rides the query.
 */
const login = Object.assign(({ next } = {}) => withNext('/login', next), {
    link: ({ token, next }) => withNext(`/login/link/${token}`, next),
});
/**
 * The doors: the pages a PERSON stands on before an account is known, one
 * builder per door segment and named for it: signing in and out, joining an
 * account, granting an app access. A door's object rides its path (the
 * sign-in link's token, the invitation's id), and the journey rides the
 * query, so the three doors a sign-in moves through take it by name. What a
 * person does IN an account is a section (`consolePaths`), the claim among
 * them.
 *
 * ```ts
 * doors.login.link({ token, next: '/claim/abc' })
 * // '/login/link/<token>?next=%2Fclaim%2Fabc'
 * ```
 */
export const doors = {
    login,
    register: ({ next } = {}) => withNext('/register', next),
    logout: ({ next } = {}) => withNext('/logout', next),
    invitation: (invitation) => `/invitation/${invitation}`,
    consent: () => '/consent',
};
/**
 * Which Stripe return a query string marks: `checkout` for the success page
 * after Checkout, `portal` for settings after the Customer Portal, or null.
 */
export function billingReturnOf(search) {
    const params = new URLSearchParams(search);
    for (const [kind, [key, value]] of Object.entries(BILLING_RETURNS)) {
        if (params.get(key) === value)
            return kind;
    }
    return null;
}
const isDoor = (segment) => DOOR_SEGMENTS.includes(segment);
const isSection = (segment) => Object.hasOwn(SECTIONS, segment);
function placeOf(path) {
    const pathname = path.split(/[?#]/)[0].toLowerCase();
    const [first = '', second = ''] = pathname.split('/').filter(Boolean);
    if (isDoor(first))
        return { kind: 'door', door: first };
    if (first === ADMIN_SEGMENT)
        return { kind: 'admin' };
    if (first === '' || isSection(first)) {
        return { kind: 'account', account: null, section: first === '' ? null : first };
    }
    return { kind: 'account', account: first, section: isSection(second) ? second : null };
}
/**
 * The account a path names, or null for a door, the operator partition, or
 * the bare form. Any first segment that is not a section, a door or the
 * partition is an account: the grammar has no other reading, and whether
 * the account exists is the server's answer, never the path's.
 */
export function accountOfPath(path) {
    const place = placeOf(path);
    return place.kind === 'account' ? place.account : null;
}
/**
 * The door a path names, or null for an account place or the operator
 * partition. Every question about doors reads this one answer: which page is
 * an auth page, which errand a destination carries, what a vendor must not
 * see. `/login/link/<token>` is the login door, as `/invitation/<id>` is the
 * invitation door: a door's object is the rest of its path.
 */
export function doorOf(path) {
    const place = placeOf(path);
    return place.kind === 'door' ? place.door : null;
}
/**
 * Whether a path names the claim section, in either form: `/claim/:code`, and
 * the same under `/<account>/`. The code is a bearer credential, which is why
 * a reader asks about this section by name: the doors title their visitor by
 * it, and analytics elides it.
 */
export const isClaimPath = (path) => {
    const place = placeOf(path);
    return place.kind === 'account' && place.section === 'claim';
};
/**
 * Whether a path names the upgrade section, in either form: `/upgrade`,
 * `/upgrade/:plan`, and the same under `/<account>/`.
 */
export const isUpgradePath = (path) => {
    const place = placeOf(path);
    return place.kind === 'account' && place.section === 'upgrade';
};
/**
 * The errand a destination path carries, in specificity order: a pricing
 * link is the broadest, and the narrower ones do not normally coexist with
 * it.
 */
export function errandOf(path) {
    if (isClaimPath(path))
        return 'claim';
    if (doorOf(path) === 'invitation')
        return 'invite';
    if (isUpgradePath(path))
        return 'upgrade';
    return null;
}
// =============================================================================
// PLATFORM CONSTANTS
// =============================================================================
/** Default API URL if not otherwise configured. */
export const DEFAULT_API = 'https://api.shipstatic.com';
/**
 * The Node SDK's ambient configuration pair — the ONLY environment variables
 * the SDK reads, and therefore the COMPLETE list an embedding host must
 * scrub (per `npm/ship`'s strict-isolation contract, scrubbing is the host's
 * job, not the SDK's). A host that derives its scrub from this object's
 * values — as the VS Code extension's child-process env block does — picks
 * up a grown contract at the next pin bump instead of by remembered prose.
 *
 * Browser builds read no environment at all, and the wrapper-tier variables
 * are deliberately NOT here: `SHIP_PASSWORD` stays the CLI's own operational
 * lever (see `npm/ship/CLAUDE.md`, "CLI-only env vars"), and the origin
 * relabel slot has its own owner beside this object ({@link SHIP_VIA_ENV})
 * because two executables read it and neither reading is the SDK's.
 */
export const SHIP_ENV = {
    /** The one credential slot — any platform token. */
    TOKEN: 'SHIP_TOKEN',
    /** The API endpoint override. */
    API_URL: 'SHIP_API_URL',
};
/**
 * The origin-relabel slot for integrations that wrap a first-party
 * executable as a subprocess. A wrapper composes an argv and an env it does
 * not otherwise reach, so this variable is its one way to name the surface
 * the traffic is really from. Two executables read it through
 * {@link normalizeVia}: the `ship` CLI (default `cli`; the GitHub Action
 * sets `git`) and the stdio MCP bin (default `mcp`; the Gemini CLI
 * extension sets `gmn`). In-process SDK consumers pass the programmatic
 * `via` option instead: same destination, different mechanism.
 *
 * Deliberately not a member of {@link SHIP_ENV}, which is the SDK's ambient
 * contract; the SDK never reads this. The name earned an owner the day the
 * stdio bin became its second reader: one spelling, two executables, and a
 * misspelling on either side drops attribution silently.
 */
export const SHIP_VIA_ENV = 'SHIP_VIA';
/**
 * Where a human creates an API key: the console deep link quoted by every
 * surface that teaches authentication (the CLI's config wizard, the VS Code
 * and n8n listings, the n8n rate-limit hint and credential copy). The bare
 * form of the path, which the console opens in the reader's own account.
 *
 * Production-branded by design: published artifacts name the product, never
 * an environment (root `CLAUDE.md`, "Environment-Aware URLs").
 */
export const MY_API_KEY_URL = `https://my.shipstatic.com${consolePaths().apiKey()}`;
/**
 * How long an anonymous deployment lives before it expires.
 *
 * The lifetime of the public tier, and one fact with several readers. The API
 * stamps a deployment's `expires` from it and gives a claim code exactly the
 * same window — a live site with a dead claim link is a coherence bug, so the
 * two are one constant rather than two that agree. Both MCP transports quote
 * the duration in prose an agent reads, and derive it from here rather than
 * writing it out, which they did in eight places until this export existed.
 *
 * Seconds, spelled in the name: this platform has both second- and
 * millisecond-valued durations, and the pair is only safe when each says which
 * it is.
 */
export const PUBLIC_DEPLOYMENT_TTL_SECONDS = 3 * 24 * 60 * 60;
/**
 * How long a build may take, start to finish: installing, building and
 * prerendering together.
 *
 * The platform reaches its build service through a proxy that ends a request
 * after 125 seconds of silence, and only an Enterprise plan can change that,
 * so a build answers inside this limit or is refused with a sentence that
 * names it. One number with several readers: the API waits a little longer
 * than it before giving up, the SDK waits for the upload plus it, the build
 * service enforces it (its `config.env`, held to this by `builds/check.sh`),
 * and the docs state it in minutes (held by `scripts/check-doc-limits.sh`).
 *
 * The day builds are sold by plan, this moves to `/limits`, where a plan's
 * numbers are delivered.
 */
export const BUILD_TIME_LIMIT_SECONDS = 120;
// =============================================================================
// TIME REMAINING
// =============================================================================
/**
 * A span of time in words: `"3 days"`, `"5 hours"`, `"45 minutes"`.
 *
 * Minutes under two hours, hours under two days, and days beyond, each rounded
 * to the nearest. The unit changes exactly where the larger one rounds to two,
 * so the number never jumps as time passes (119 minutes, then 2 hours), and
 * the two vaguest things a rounded clock can say, "1 hour" and "1 day", are
 * never said. Rounding rather than truncating is what lets a deployment made a
 * second ago read the lifetime it was given: "3 days", not "2 days". Days are
 * the largest unit because nobody counts a deadline in weeks, and a month has
 * no fixed length.
 *
 * A span that rounds to no minutes at all, zero and negative included, reads
 * "less than a minute".
 */
export function formatDuration(seconds) {
    const minutes = Math.round(seconds / 60);
    if (!(minutes >= 1))
        return 'less than a minute';
    if (minutes < 120)
        return countOf(minutes, 'minute');
    const hours = Math.round(seconds / 3_600);
    if (hours < 48)
        return countOf(hours, 'hour');
    return countOf(Math.round(seconds / 86_400), 'day');
}
/**
 * The time left before a deadline, in words (`"3 days"`), or `null` once it
 * has passed.
 *
 * `expires` is unix SECONDS, the wire's own field, taken as it arrives so that
 * no caller converts it: the deploy card once read it as a date string and
 * showed the same three days on every deployment for months. `now` is seconds
 * too, and defaults to the clock.
 *
 * A surface that tells a person how long a deployment has left says it with
 * this, so one deployment reads the same everywhere, whenever it is looked at.
 * What a surface says around the words is its own ("Expires in", "It stays
 * live for"), and so is what it says once the deadline has passed, which is
 * why that case is `null` rather than a sentence.
 */
export function formatTimeRemaining(expires, now = Date.now() / 1_000) {
    const seconds = expires - now;
    return seconds > 0 ? formatDuration(seconds) : null;
}
function countOf(count, unit) {
    return `${count} ${unit}${count === 1 ? '' : 's'}`;
}
// =============================================================================
// FILE SIZE
// =============================================================================
/**
 * A byte count as a person reads it: `"180 KB"`, `"2.5 MB"`, `"0 Bytes"`.
 *
 * Binary units, one decimal by default, and `decimals` for a surface with its
 * own density (a dense table reads `"3 MB"` at 0). A deployment's size reads
 * this way on every surface that states one: the console, the CLI, the deploy
 * card and the API's own refusals, so one deployment is one number everywhere.
 */
export function formatFileSize(bytes, decimals = 1) {
    if (bytes === 0)
        return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${Number.parseFloat((bytes / k ** i).toFixed(decimals))} ${sizes[i]}`;
}
// The billing boundary: the stream records the PLATFORM'S own acts, and
// Stripe records the subscription's life. `checkout.open` is ours — the
// platform mints the Session, and it is the one billing act that exists
// before Stripe holds anything. `account.plan.transition` is ours — what a
// change MEANS, entitlement crossing our threshold. Everything between those
// two edges (invoices, refunds, disputes, retries, scheduled changes) is the
// subscription's own history, which belongs to Stripe as merchant of record
// and is shown to the customer in the Customer Portal; none of it is
// restated here. Refusals write no history either: the stream records what
// happened to resources, never what was declined — a detection
// (`deployment.flagged`) is a fact discovered about content, not a refusal.
/**
 * The events an account's own feed shows: curated meaning, where everything
 * else in the stream is the operator's forensics. One list, the owner of
 * both the API's filter and the type; the retention floor covers exactly
 * these, since the floor exists to keep a dormant account's feed from
 * reading empty.
 */
export const USER_VISIBLE_EVENTS = [
    'account.create',
    'account.update',
    'account.delete',
    'account.key.generate',
    'account.plan.transition',
    'deployment.create',
    'deployment.update',
    'deployment.delete',
    'deployment.claim',
    'domain.create',
    'domain.update',
    'domain.delete',
    'domain.verify',
    'token.create',
    'token.consume',
    'token.delete',
    'member.join',
    'member.leave',
    'consent.grant',
];
// =============================================================================
// FILE UPLOAD TYPES
// =============================================================================
/**
 * File status constants for validation state tracking
 */
export const FileValidationStatus = {
    /** File is pending validation */
    PENDING: 'pending',
    /** File failed during processing (before validation) */
    PROCESSING_ERROR: 'processing_error',
    /** File was excluded by validation warning (not an error) */
    EXCLUDED: 'excluded',
    /** File failed validation (blocks deployment) */
    VALIDATION_FAILED: 'validation_failed',
    /** File passed validation and is ready for deployment */
    READY: 'ready',
};
// =============================================================================
// DOMAIN UTILITIES
// =============================================================================
/**
 * Check if a domain is a platform domain (subdomain of our platform).
 * Platform domains are free and don't require DNS verification.
 *
 * @example isPlatformDomain("www.shipstatic.com", "shipstatic.com") → true
 * @example isPlatformDomain("example.com", "shipstatic.com") → false
 */
export function isPlatformDomain(domain, platformDomain) {
    return domain.endsWith(`.${platformDomain}`);
}
/**
 * Check if a domain is a custom domain (not a platform domain).
 * Custom domains are billable and require DNS verification.
 *
 * @example isCustomDomain("example.com", "shipstatic.com") → true
 * @example isCustomDomain("www.shipstatic.com", "shipstatic.com") → false
 */
export function isCustomDomain(domain, platformDomain) {
    return !isPlatformDomain(domain, platformDomain);
}
/**
 * Extract subdomain from a platform domain.
 * Returns null if not a platform domain.
 *
 * @example extractSubdomain("www.shipstatic.com", "shipstatic.com") → "www"
 * @example extractSubdomain("example.com", "shipstatic.com") → null
 */
export function extractSubdomain(domain, platformDomain) {
    if (!isPlatformDomain(domain, platformDomain)) {
        return null;
    }
    return domain.slice(0, -(platformDomain.length + 1)); // +1 for the dot
}
/**
 * Generate HTTPS URL for a deployment hostname.
 */
export function generateDeploymentUrl(deployment) {
    return `https://${deployment}`;
}
/**
 * Generate HTTPS URL for a domain.
 */
export function generateDomainUrl(domain) {
    return `https://${domain}`;
}
// =============================================================================
// LABEL UTILITIES
// =============================================================================
/**
 * A label is a short identifier attached to a deployment, a domain or a token.
 * It has one of two forms:
 *
 * - a word: `production`, `v1.2.3`, `us-east-1`
 * - a key and a value, split by one `:`: `env:production`, `team:web`
 *
 * Each side is lowercase letters and digits, with single `.`, `_` or `-`
 * between them. The whole label, the key and the `:` and the value together,
 * counts toward {@link LABEL_CONSTRAINTS.MAX_LENGTH}.
 *
 * The platform stores a label in lowercase and shows it as stored. It reads
 * nothing from a key or a value and changes no behaviour because of one: a
 * label describes a resource and lets its owner find it. So a resource may carry several
 * values for one key (`team:web` and `team:api`), and only the same label
 * twice is refused.
 *
 * Labels stay a list of strings on the wire and in storage. A caller that
 * wants a key's value splits on the `:` itself.
 */
export const LABEL_CONSTRAINTS = {
    /** Minimum label length in characters. */
    MIN_LENGTH: 3,
    /**
     * Maximum label length in characters. A key, its `:` and its value share it,
     * and it fits a key with a UUID (36 characters) or a full git SHA (40).
     */
    MAX_LENGTH: 63,
    /** Maximum number of labels on one resource. */
    MAX_COUNT: 10,
};
/**
 * The shape of a label: one side, or two with a `:` between them. A side is
 * lowercase letters and digits with single `.`, `_` or `-` between them, and
 * the pattern spells it twice. Length is {@link LABEL_CONSTRAINTS}' rule, and
 * {@link validateLabel} applies both.
 *
 * Matches: `production`, `v1.2.3`, `env:production`, `commit:3f9c2ab`
 * Refuses: `-prod`, `foo--bar`, `:production`, `env:`, `a:b:c`, `Env:Prod`
 */
export const LABEL_PATTERN = /^[a-z0-9]+(?:[._-][a-z0-9]+)*(?::[a-z0-9]+(?:[._-][a-z0-9]+)*)?$/;
/**
 * Validate one label and return it normalized: trimmed and lowercased, then
 * held to {@link LABEL_CONSTRAINTS}' length and to {@link LABEL_PATTERN}.
 * Throws `ShipError.validation` when it does not fit.
 *
 * Lowercasing is the one normalization and it applies to both sides of a
 * `:`, so `Env:Prod` is stored as `env:prod`. A value that must keep its
 * case does not belong in a label.
 *
 * The one label check on the platform: the SDK and the console call it for
 * fast feedback and the API calls it as the boundary, so all three refuse
 * the same strings in the same words.
 */
export function validateLabel(value) {
    if (typeof value !== 'string') {
        throw ShipError.validation('Labels must be strings');
    }
    const label = value.trim().toLowerCase();
    if (label.length < LABEL_CONSTRAINTS.MIN_LENGTH) {
        throw ShipError.validation(`Labels must be at least ${LABEL_CONSTRAINTS.MIN_LENGTH} characters long`);
    }
    if (label.length > LABEL_CONSTRAINTS.MAX_LENGTH) {
        throw ShipError.validation(`Labels must be no more than ${LABEL_CONSTRAINTS.MAX_LENGTH} characters long`);
    }
    if (!LABEL_PATTERN.test(label)) {
        throw ShipError.validation('Labels use letters and numbers, with . _ or - between them, and can have one : between a key and a value, like env:production');
    }
    return label;
}
export function validateLabels(labels) {
    if (labels === undefined || labels === null)
        return undefined;
    if (!Array.isArray(labels)) {
        throw ShipError.validation('Labels must be an array');
    }
    if (labels.length > LABEL_CONSTRAINTS.MAX_COUNT) {
        throw ShipError.validation(`Maximum ${LABEL_CONSTRAINTS.MAX_COUNT} labels allowed`);
    }
    const normalized = labels.map(validateLabel);
    if (new Set(normalized).size !== normalized.length) {
        throw ShipError.validation('Duplicate labels are not allowed');
    }
    return normalized;
}
/**
 * Serialize labels array to JSON string for database storage.
 * Returns null for empty or undefined arrays.
 *
 * @example serializeLabels(['web', 'production']) → '["web","production"]'
 * @example serializeLabels([]) → null
 * @example serializeLabels(undefined) → null
 */
export function serializeLabels(labels) {
    if (!labels || labels.length === 0)
        return null;
    return JSON.stringify(labels);
}
/**
 * Deserialize labels from JSON string to array.
 * Always returns an array — empty array for null/empty/invalid input.
 *
 * @example deserializeLabels('["web","production"]') → ['web', 'production']
 * @example deserializeLabels(null) → []
 * @example deserializeLabels('') → []
 */
export function deserializeLabels(labelsJson) {
    if (!labelsJson)
        return [];
    try {
        const parsed = JSON.parse(labelsJson);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
// =============================================================================
// BUILD SETTINGS
// =============================================================================
/**
 * The format of the two per-deploy build settings a caller may name when the
 * builder's own detection is not enough: the command to run and the folder
 * the site lands in. Format rules only, the format/policy split this file
 * keeps everywhere: WHAT a value may look like lives here so both the console
 * (fast feedback) and the API (the boundary) refuse the same strings; whether
 * the command builds anything is the build's verdict, not a rule.
 *
 * Both are read only by first-party `/upload`, only with `build`, and both
 * run inside the throwaway container that already runs the project's own
 * arbitrary code, which is why the command's format is a shape rule (one
 * line, bounded) and not a safety rule. The folder's rule is what keeps it a
 * folder OF the project: relative, no parent segments, no leading slash.
 */
export const BUILD_SETTINGS = {
    /** A build command is one line, non-empty, and bounded. */
    COMMAND_MAX_LENGTH: 200,
    /** An output folder is a bounded relative path. */
    OUTPUT_DIR_MAX_LENGTH: 100,
    /** Path segments and separators only: `dist`, `dist/site`, `.output/public`. */
    OUTPUT_DIR_PATTERN: /^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*$/,
};
/**
 * Validate an optional build command and return it normalized (trimmed).
 * Absent → `undefined`. Present → one line, 1 to
 * {@link BUILD_SETTINGS.COMMAND_MAX_LENGTH} characters, no control characters.
 */
export function validateBuildCommand(value) {
    if (value === undefined || value === null || value === '')
        return undefined;
    if (typeof value !== 'string') {
        throw ShipError.validation('Build command must be a string');
    }
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > BUILD_SETTINGS.COMMAND_MAX_LENGTH) {
        throw ShipError.validation(`Build command must be between 1 and ${BUILD_SETTINGS.COMMAND_MAX_LENGTH} characters`);
    }
    // biome-ignore lint/suspicious/noControlCharactersInRegex: a command is one line; a newline or any other control character is a second one
    if (/[\x00-\x1f\x7f]/.test(trimmed)) {
        throw ShipError.validation('Build command must be a single line');
    }
    return trimmed;
}
/**
 * Validate an optional output folder and return it normalized (trimmed, no
 * trailing slash). Absent → `undefined`. Present → a relative path of plain
 * segments, no `..`, no leading slash, at most
 * {@link BUILD_SETTINGS.OUTPUT_DIR_MAX_LENGTH} characters.
 */
export function validateOutputDir(value) {
    if (value === undefined || value === null || value === '')
        return undefined;
    if (typeof value !== 'string') {
        throw ShipError.validation('Output folder must be a string');
    }
    const trimmed = value.trim().replace(/\/+$/, '');
    if (trimmed.length === 0 || trimmed.length > BUILD_SETTINGS.OUTPUT_DIR_MAX_LENGTH) {
        throw ShipError.validation(`Output folder must be between 1 and ${BUILD_SETTINGS.OUTPUT_DIR_MAX_LENGTH} characters`);
    }
    if (!BUILD_SETTINGS.OUTPUT_DIR_PATTERN.test(trimmed) ||
        trimmed.split('/').some((segment) => segment === '..' || segment === '.')) {
        throw ShipError.validation('Output folder must be a relative path inside the project, like dist or dist/site');
    }
    return trimmed;
}
// =============================================================================
// PASSWORD UTILITIES
// =============================================================================
/**
 * Length constraints for the optional deployment password
 * (`DeploymentUploadOptions.password`). Single source of truth shared across
 * platform consumers.
 */
export const PASSWORD_CONSTRAINTS = {
    /** Minimum password length in characters */
    MIN_LENGTH: 6,
    /** Maximum password length in characters */
    MAX_LENGTH: 128,
};
/**
 * Validate an optional deployment password and return it normalized.
 *
 * Absent (`undefined` / `null`) → returns `undefined`. Present → trim
 * leading/trailing whitespace, then validate against `PASSWORD_CONSTRAINTS`
 * length bounds (internal whitespace is significant and counts toward
 * length). Throws `ShipError.validation` on breach; returns the trimmed
 * value.
 *
 * The trim is canonical: at upload, the API hashes the trimmed value; at
 * unlock, the router trims submissions before hashing. Submission and storage
 * agree byte-for-byte. Length validation runs on the trimmed value because
 * that's the user's actual intent — and it disarms a class of invisible
 * foot-guns (trailing newlines from copy/paste, mobile auto-spacing,
 * password-manager artifacts).
 *
 * Single source of truth shared by SDK (client-side validation, return
 * ignored) and API (server-side enforcement, return threaded into config).
 * Length is part of the wire-format contract; strength rules, if added later,
 * stay server-side. See `CLAUDE.md` "Validation: format vs policy".
 */
export function validatePassword(value) {
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== 'string') {
        throw ShipError.validation('Password must be a string');
    }
    const trimmed = value.trim();
    if (trimmed.length < PASSWORD_CONSTRAINTS.MIN_LENGTH ||
        trimmed.length > PASSWORD_CONSTRAINTS.MAX_LENGTH) {
        throw ShipError.validation(`Password must be between ${PASSWORD_CONSTRAINTS.MIN_LENGTH} and ${PASSWORD_CONSTRAINTS.MAX_LENGTH} characters`);
    }
    return trimmed;
}
